/**
 * @module services/gitSyncService
 * @description Git sync service for Preparo — persistence via Gitea
 *
 * Tracks local changes (dirty tracking), debounces saves, serializes
 * IndexedDB data to Git file format, and pushes to Gitea via the
 * Edge Function `git-sync`.
 *
 * Architecture:
 * - IndexedDB remains the local cache (source of truth for the UI)
 * - Git (Gitea) is the persistent backend (source of truth for data)
 * - This service bridges the two with debounced, queued pushes
 *
 * @example
 * import { markProjectDirty, syncNow, syncStatus } from '@services/gitSyncService';
 *
 * // After saving a document
 * markProjectDirty(projectId, 'document');
 *
 * // Force immediate sync
 * await syncNow();
 */

import { writable, derived, get } from 'svelte/store';
import { api, sync } from '@/config';
import { getAccessToken, getSupabaseClient } from '@services/supabaseClient';
import { db } from '@services/indexedDBService';
import { createLogger } from '@services/loggerService';
import type {
  Project,
  Document,
  Image,
  Template,
  Preset,
  TemplateSection,
  TemplateType,
} from '@/types';
import { generateFilenames } from '@/utils/fileNaming';

const log = createLogger('gitSync');

// ============================================================================
// TYPES
// ============================================================================

/** Sync status for the UI indicator */
export type SyncStatusValue = 'idle' | 'syncing' | 'error' | 'offline' | 'disabled';

/** Type of change that triggers sync */
export type ChangeType = 'document' | 'metadata' | 'config' | 'template' | 'image' | 'preset';

/** Merge action determined by 3-way hash comparison */
export type MergeAction = 'identical' | 'push' | 'pull' | 'new_remote' | 'new_local' | 'conflict';

/** A file-level conflict detected during hydration */
export interface SyncConflict {
  /** Unique conflict ID */
  id: string;
  /** Project ID this conflict belongs to */
  projectId: string;
  /** Relative file path from repo root */
  filePath: string;
  /** Local content at time of conflict detection */
  localContent: string;
  /** Remote content from Git */
  remoteContent: string;
  /** Content hash at last successful sync */
  lastSyncedHash: string;
  /** Current local content hash */
  localHash: string;
  /** Current remote content hash */
  remoteHash: string;
  /** Timestamp when conflict was detected */
  detectedAt: string;
  /** Whether this conflict has been resolved */
  resolved: boolean;
}

/** Hydration state for a project (lazy loading) */
export type ProjectHydrationState = 'synced' | 'stale' | 'hydrating' | 'conflict' | 'local_only' | 'unknown';

/** Index entry from manifest comparison at startup */
export interface ProjectIndexEntry {
  id: string;
  /** Last modified timestamp from remote manifest */
  remoteLastModified: string | null;
  /** Last modified timestamp from local project */
  localLastModified: string | null;
  /** Whether this project needs hydration */
  hydrationState: ProjectHydrationState;
  /** Title extracted from .preparo.json or manifest (for stub creation) */
  title?: string;
  /** Project type from .preparo.json */
  projectType?: 'book' | 'article';
  /** Aggregate tree hash from server (all files under project dir) */
  treeHash?: string | null;
}

/** A single file change to push to Git */
export interface FileChange {
  /** Relative path from repo root (e.g. "projects/uuid/texts/01-chapter.md") */
  path: string;
  /** Operation type */
  operation: 'create' | 'update' | 'delete';
  /** File content (UTF-8 text or base64 for binaries) */
  content?: string;
  /** Content encoding */
  encoding?: 'text' | 'base64';
}

/** Result of a push operation */
export interface PushResult {
  success: boolean;
  commitSha?: string;
  filesChanged?: number;
  error?: string;
  /** Error code from the Edge Function */
  code?: string;
  /** Retry delay in ms (for 429 responses) */
  retryAfterMs?: number;
  /** Updated tree hashes per project (projectId → treeHash) */
  treeHashes?: Record<string, string | null>;
}

/** Result of a getStatus call */
export interface GitStatusResult {
  provisioned: boolean;
  giteaUsername?: string;
  repoName?: string;
  repoUrl?: string;
  lfsEnabled?: boolean;
  lastCommitSha?: string | null;
}

/** Result from listProjects */
export interface GitProjectEntry {
  id: string;
  manifestPath: string;
  manifest: string | null;
  preparoPath: string;
  preparo: string | null;
  metadata: string | null;
  /** Aggregate SHA-256 of all blob SHAs under this project's Git tree.
   *  Covers all files (config/*.json, texts/*.md, assets/*, etc.).
   *  null when the tree API call failed or project dir is empty. */
  treeHash: string | null;
}

/** Result from getProject */
export interface GitProjectFile {
  path: string;
  content: string;
  encoding: 'text' | 'base64';
  size: number;
}

/** Internal dirty state per project */
interface DirtyEntry {
  projectId: string;
  changeTypes: Set<ChangeType>;
  /** Timestamp of the first change in this batch */
  firstChangeAt: number;
}

/** Persisted sync state for a project or preset (stored in IndexedDB settings) */
interface SyncState {
  /** SHA-256 hashes of file contents at last successful sync */
  fileHashes: Record<string, string>;
  /** SHA of the last successful commit */
  lastCommitSha: string | null;
  /** Raw YAML sections from manifest.yaml that Preparo doesn't own (outputs, buildInfo, etc.) */
  manifestExtras?: string;
  /** Baseline file contents for mergeable files (config/*.json, metadata.yaml) — used for 3-way merge */
  baselineContents?: Record<string, string>;
  /** Aggregate tree hash from the server (covers ALL files under the project dir) */
  treeHash?: string | null;
}

/** Result of serialization including computed hashes for post-push state update */
interface SerializationResult {
  /** Only the files that actually changed (or were deleted) */
  changes: FileChange[];
  /** SHA-256 hashes of ALL current files (for saving after successful push) */
  currentHashes: Record<string, string>;
  /** Contents of ALL current files (path → content), for 3-way merge in hydrateProject */
  currentContents: Map<string, string>;
  /** IndexedDB settings key for this project/preset's sync state */
  syncStateKey: string;
}

// ============================================================================
// STORES
// ============================================================================

/** Current sync status for UI display */
export const syncStatus = writable<SyncStatusValue>(sync.enabled ? 'idle' : 'disabled');

/** Number of pending changes waiting to be synced */
export const pendingChangesCount = writable<number>(0);

/** ISO timestamp of last successful sync */
export const lastSyncedAt = writable<string | null>(null);

/** Last sync error message (null if no error) */
export const lastSyncError = writable<string | null>(null);

/** Whether the user is provisioned on Gitea */
export const isProvisioned = writable<boolean>(false);

/** Whether sync is currently enabled and the user is authenticated */
export const isSyncActive = derived(
  [syncStatus],
  ([$syncStatus]) => $syncStatus !== 'disabled'
);

/** Active conflicts (file-level, per project) */
export const syncConflicts = writable<SyncConflict[]>([]);

/** Number of unresolved conflicts */
export const conflictCount = derived(
  [syncConflicts],
  ([$syncConflicts]) => $syncConflicts.filter(c => !c.resolved).length
);

/** Project hydration index — maps projectId → hydration state */
export const projectIndex = writable<Map<string, ProjectIndexEntry>>(new Map());

/** Last hydration result — bumped after each hydrateFromGit() call.
 *  UI components can subscribe to refresh stores when new items arrive. */
export const lastHydrationResult = writable<HydrationResult | null>(null);

/** Whether the browser is online */
export const isOnline = writable<boolean>(
  typeof navigator !== 'undefined' ? navigator.onLine : true
);

// ============================================================================
// INTERNAL STATE
// ============================================================================

/** Queue of dirty projects waiting to be synced */
const dirtyQueue = new Map<string, DirtyEntry>();

/** Dirty presets waiting to be synced */
const dirtyPresets = new Set<string>();

/** Content debounce timer */
let contentTimer: ReturnType<typeof setTimeout> | null = null;

/** Structure debounce timer */
let structureTimer: ReturnType<typeof setTimeout> | null = null;

/** Force sync timer (max delay between syncs during continuous writing) */
let forceTimer: ReturnType<typeof setTimeout> | null = null;

/** Whether a sync operation is currently in progress */
let isSyncing = false;

/** Consecutive 409 CONFLICT retry counter (reset on success or non-conflict error) */
let conflictRetryCount = 0;
const MAX_CONFLICT_RETRIES = 3;

// ============================================================================
// CONTENT HASHING — SHA-256 for intelligent dirty tracking
// ============================================================================

/**
 * Compute SHA-256 hash of a string using Web Crypto API.
 * Used to detect unchanged files and skip redundant pushes.
 */
async function computeHash(content: string): Promise<string> {
  const data = new TextEncoder().encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < hashArray.length; i++) {
    hex += hashArray[i].toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Load persisted sync state for a project or preset from IndexedDB.
 */
async function loadSyncState(key: string): Promise<SyncState> {
  const stored = await db.settings.get<SyncState | null>(key, null);
  if (!stored) return { fileHashes: {}, lastCommitSha: null, manifestExtras: '', baselineContents: {} };
  // Ensure new fields have defaults for states saved before these fields existed
  return {
    ...stored,
    manifestExtras: stored.manifestExtras ?? '',
    baselineContents: stored.baselineContents ?? {},
  };
}

/**
 * Save sync state to IndexedDB after a successful push.
 */
async function saveSyncState(key: string, state: SyncState): Promise<void> {
  await db.settings.set(key, state);
}

/**
 * Remove persisted sync state from IndexedDB (used on project/preset deletion).
 */
async function removeSyncState(key: string): Promise<void> {
  await db.settings.delete(key);
}

// ============================================================================
// EDGE FUNCTION COMMUNICATION
// ============================================================================

/** Timeout for git-sync fetch requests (30 seconds) */
const GIT_SYNC_TIMEOUT_MS = 30_000;

/**
 * Call the git-sync Edge Function
 */
async function callGitSync<T>(body: Record<string, unknown>): Promise<T> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const url = `${api.proxy.supabaseUrl}/functions/v1/${sync.edgeFunctionName}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GIT_SYNC_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'apikey': api.proxy.supabaseAnonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new GitSyncError(
        `Request timed out after ${GIT_SYNC_TIMEOUT_MS / 1000}s`,
        'TIMEOUT',
        0
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (response.status === 412 && errorData.code === 'NOT_PROVISIONED') {
      isProvisioned.set(false);
      throw new GitSyncError('User not provisioned', 'NOT_PROVISIONED', response.status);
    }

    if (response.status === 429) {
      throw new GitSyncError(
        errorData.error || 'Rate limit exceeded',
        'RATE_LIMITED',
        429,
        errorData.retryAfterMs
      );
    }

    if (response.status === 409) {
      throw new GitSyncError(
        errorData.error || 'Conflict detected',
        'CONFLICT',
        409
      );
    }

    throw new GitSyncError(
      errorData.error || `git-sync request failed: ${response.status}`,
      errorData.code || 'UNKNOWN',
      response.status
    );
  }

  return response.json();
}

/** Custom error class for git-sync errors */
export class GitSyncError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'GitSyncError';
  }
}

// ============================================================================
// PUBLIC API — STATUS & READ OPERATIONS
// ============================================================================

/**
 * Check provisioning status and return repo state.
 * If not provisioned, the git-provision Edge Function should be called first.
 */
export async function checkStatus(): Promise<GitStatusResult> {
  log.debug('Checking git sync status');

  const result = await callGitSync<GitStatusResult>({ action: 'getStatus' });
  isProvisioned.set(result.provisioned);

  if (result.provisioned) {
    log.info('Git sync status OK', {
      username: result.giteaUsername,
      repo: result.repoName,
    });
  } else {
    log.warn('User not provisioned on Gitea');
  }

  return result;
}

/**
 * List all projects from the Git repo (manifests only, lightweight).
 */
export async function listProjects(): Promise<GitProjectEntry[]> {
  log.debug('Listing projects from Git');

  const result = await callGitSync<{ projects: GitProjectEntry[] }>({
    action: 'listProjects',
  });

  log.info('Projects listed from Git', { count: result.projects.length });
  return result.projects;
}

/**
 * Fetch all files for a single project from Git.
 */
export async function getProject(projectId: string): Promise<GitProjectFile[]> {
  log.debug('Fetching project from Git', { projectId });

  const result = await callGitSync<{ projectId: string; files: GitProjectFile[] }>({
    action: 'getProject',
    projectId,
  });

  log.info('Project fetched from Git', { projectId, fileCount: result.files.length });
  return result.files;
}

/**
 * List all presets from the Git repo.
 */
export async function listPresets(): Promise<{ dirName: string; presetJsonPath: string; presetJson: string | null }[]> {
  log.debug('Listing presets from Git');

  const result = await callGitSync<{ presets: { dirName: string; presetJsonPath: string; presetJson: string | null }[] }>({
    action: 'getPresets',
  });

  log.info('Presets listed from Git', { count: result.presets.length });
  return result.presets;
}

/**
 * Fetch all files for a single preset from Git.
 */
export async function getPreset(presetDir: string): Promise<GitProjectFile[]> {
  log.debug('Fetching preset from Git', { presetDir });

  const result = await callGitSync<{ presetDir: string; files: GitProjectFile[] }>({
    action: 'getPreset',
    presetDir,
  });

  log.info('Preset fetched from Git', { presetDir, fileCount: result.files.length });
  return result.files;
}

// ============================================================================
// PUBLIC API — WRITE OPERATIONS
// ============================================================================

/**
 * Push a set of file changes to Git atomically.
 */
export async function pushChanges(
  changes: FileChange[],
  commitMessage: string
): Promise<PushResult> {
  if (changes.length === 0) {
    return { success: true, filesChanged: 0 };
  }

  log.debug('Pushing changes to Git', { fileCount: changes.length, commitMessage });

  try {
    const result = await callGitSync<{
      commitSha: string;
      filesChanged: number;
      treeHashes?: Record<string, string | null>;
    }>({
      action: 'pushChanges',
      commitMessage,
      changes,
    });

    log.info('Changes pushed to Git', {
      commitSha: result.commitSha,
      filesChanged: result.filesChanged,
    });

    return {
      success: true,
      commitSha: result.commitSha,
      filesChanged: result.filesChanged,
      treeHashes: result.treeHashes,
    };
  } catch (error) {
    if (error instanceof GitSyncError) {
      return {
        success: false,
        error: error.message,
        code: error.code,
        retryAfterMs: error.retryAfterMs,
      };
    }
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Delete a project from Git.
 */
export async function deleteProjectFromGit(projectId: string): Promise<PushResult> {
  log.debug('Deleting project from Git', { projectId });

  try {
    const result = await callGitSync<{ commitSha: string; filesDeleted: number }>({
      action: 'deleteProject',
      projectId,
    });

    log.info('Project deleted from Git', { projectId, commitSha: result.commitSha });

    // Clean up persisted sync state
    await removeSyncState(`gitSync:project:${projectId}`);

    return { success: true, commitSha: result.commitSha, filesChanged: result.filesDeleted };
  } catch (error) {
    const msg = (error as Error).message;
    log.error('Failed to delete project from Git', { projectId, error: msg });
    return { success: false, error: msg };
  }
}

/**
 * Delete a preset from Git.
 */
export async function deletePresetFromGit(presetDir: string): Promise<PushResult> {
  log.debug('Deleting preset from Git', { presetDir });

  try {
    const result = await callGitSync<{ commitSha: string; filesDeleted: number }>({
      action: 'deletePreset',
      presetDir,
    });

    log.info('Preset deleted from Git', { presetDir, commitSha: result.commitSha });

    // Clean up persisted sync state (extract presetId from presetDir: "presetId_slug")
    const presetId = presetDir.split('_')[0];
    if (presetId) {
      await removeSyncState(`gitSync:preset:${presetId}`);
    }

    return { success: true, commitSha: result.commitSha, filesChanged: result.filesDeleted };
  } catch (error) {
    const msg = (error as Error).message;
    log.error('Failed to delete preset from Git', { presetDir, error: msg });
    return { success: false, error: msg };
  }
}

// ============================================================================
// DIRTY TRACKING & DEBOUNCE
// ============================================================================

/**
 * Mark a project as modified. Called by appStore / editorStore after each save.
 *
 * Selects the appropriate debounce based on the change type:
 * - 'document' → DEBOUNCE_CONTENT (60s default)
 * - everything else → DEBOUNCE_STRUCTURE (5s default)
 *
 * A force timer guarantees a sync within DEBOUNCE_FORCE (5min default)
 * even during continuous writing.
 */
export function markProjectDirty(projectId: string, changeType: ChangeType): void {
  if (!sync.enabled) return;

  const existing = dirtyQueue.get(projectId);
  if (existing) {
    existing.changeTypes.add(changeType);
  } else {
    dirtyQueue.set(projectId, {
      projectId,
      changeTypes: new Set([changeType]),
      firstChangeAt: Date.now(),
    });
  }

  pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);

  // Select debounce delay based on change type
  const isStructural = changeType !== 'document';
  const delay = isStructural ? sync.debounceStructureMs : sync.debounceContentMs;

  // Reset the appropriate timer
  if (isStructural) {
    if (structureTimer) clearTimeout(structureTimer);
    structureTimer = setTimeout(() => {
      structureTimer = null;
      void flushQueue();
    }, delay);
  } else {
    if (contentTimer) clearTimeout(contentTimer);
    contentTimer = setTimeout(() => {
      contentTimer = null;
      void flushQueue();
    }, delay);
  }

  // Ensure force timer is running (max delay between syncs)
  if (!forceTimer) {
    forceTimer = setTimeout(() => {
      forceTimer = null;
      void flushQueue();
    }, sync.debounceForceMs);
  }

  log.debug('Project marked dirty', { projectId, changeType, queueSize: dirtyQueue.size });
}

/**
 * Mark a preset as modified.
 */
export function markPresetDirty(presetId: string): void {
  if (!sync.enabled) return;

  dirtyPresets.add(presetId);
  pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);

  // Presets use the structural debounce (fast)
  if (structureTimer) clearTimeout(structureTimer);
  structureTimer = setTimeout(() => {
    structureTimer = null;
    void flushQueue();
  }, sync.debounceStructureMs);

  if (!forceTimer) {
    forceTimer = setTimeout(() => {
      forceTimer = null;
      void flushQueue();
    }, sync.debounceForceMs);
  }

  log.debug('Preset marked dirty', { presetId });
}

/**
 * Detect local presets that have never been synced to Git and mark them dirty.
 * Should be called once after init() succeeds, so that pre-existing presets
 * (created before git sync was enabled) get pushed on first opportunity.
 */
export async function markUnsyncedPresetsDirty(): Promise<void> {
  if (!sync.enabled) return;

  try {
    const allPresets = await db.presets.getAll();
    let marked = 0;

    for (const preset of allPresets) {
      const syncState = await loadSyncState(`gitSync:preset:${preset.id}`);
      // If no file hashes recorded, this preset has never been synced
      if (Object.keys(syncState.fileHashes).length === 0) {
        dirtyPresets.add(preset.id);
        marked++;
      }
    }

    if (marked > 0) {
      pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);
      log.info('Marked unsynced presets as dirty', { count: marked });

      // Trigger a sync with the structural debounce
      if (structureTimer) clearTimeout(structureTimer);
      structureTimer = setTimeout(() => {
        structureTimer = null;
        void flushQueue();
      }, sync.debounceStructureMs);
    }
  } catch (error) {
    log.warn('Failed to check unsynced presets', { error: (error as Error).message });
  }
}

/**
 * Force an immediate sync of all pending changes.
 */
export async function syncNow(): Promise<PushResult> {
  clearAllTimers();
  return flushQueue();
}

// ============================================================================
// SERIALIZATION — IndexedDB → Git file format
// ============================================================================

/**
 * Serialize a project from IndexedDB into FileChange[] for Git.
 * Uses SHA-256 hashes to only include files that actually changed
 * since the last successful sync.
 */
async function serializeProject(projectId: string): Promise<SerializationResult> {
  const syncStateKey = `gitSync:project:${projectId}`;

  const project = await db.projects.get(projectId);
  if (!project) {
    log.warn('Project not found for serialization', { projectId });
    return { changes: [], currentHashes: {}, currentContents: new Map(), syncStateKey };
  }

  const documents = await db.documents.getByProject(projectId);
  const projectImages = await db.images.getByProject(projectId);
  const projectTemplates = await db.templates.getByProject(projectId);

  // Load sync state to get preserved manifest extras (outputs, buildInfo from AutomaticBook)
  const previousState = await loadSyncState(syncStateKey);

  const dir = `projects/${project.id}`;

  // Build all current files with their content
  const currentFiles: { path: string; content: string; encoding: 'text' | 'base64' }[] = [];

  // manifest.yaml — pass preserved extras from last hydration
  currentFiles.push({
    path: `${dir}/manifest.yaml`,
    content: generateManifestYaml(project, documents, projectTemplates, previousState.manifestExtras),
    encoding: 'text',
  });

  // .preparo.json
  currentFiles.push({
    path: `${dir}/.preparo.json`,
    content: generatePreparoJson(project, documents),
    encoding: 'text',
  });

  // metadata.yaml
  if (project.metadata) {
    currentFiles.push({
      path: `${dir}/metadata.yaml`,
      content: project.metadata,
      encoding: 'text',
    });
  }

  // texts/*.md — document contents (sorted by section, then order)
  const filenameResults = generateFilenames(documents);
  for (const { doc, filename } of filenameResults) {
    const content = await db.contents.get(doc.id);
    currentFiles.push({
      path: `${dir}/texts/${filename}`,
      content: content || '',
      encoding: 'text',
    });
  }

  // config/*.json and template/**/* — templates
  for (const tpl of projectTemplates) {
    const filePath = getTemplateGitPath(dir, tpl);
    if (!filePath) continue;

    currentFiles.push({
      path: filePath,
      content: tpl.content,
      encoding: isBinaryTemplate(tpl) ? 'base64' : 'text',
    });
  }

  // assets/* — images
  for (const img of projectImages) {
    const base64Data = extractBase64FromDataUrl(img.dataUrl);
    if (base64Data) {
      currentFiles.push({
        path: `${dir}/assets/${img.filename}`,
        content: base64Data,
        encoding: 'base64',
      });
    }
  }

  // Compute hash diff against previous sync state (already loaded above for manifestExtras)
  const currentHashes: Record<string, string> = {};
  const currentContents = new Map<string, string>();
  const changes: FileChange[] = [];

  for (const file of currentFiles) {
    const hash = await computeHash(file.content);
    currentHashes[file.path] = hash;
    currentContents.set(file.path, file.content);

    if (previousState.fileHashes[file.path] !== hash) {
      changes.push({
        path: file.path,
        operation: previousState.fileHashes[file.path] ? 'update' : 'create',
        content: file.content,
        encoding: file.encoding,
      });
    }
  }

  // Detect deleted files (present in last sync but not in current state)
  const dirPrefix = `${dir}/`;
  for (const prevPath of Object.keys(previousState.fileHashes)) {
    if (prevPath.startsWith(dirPrefix) && !currentHashes[prevPath]) {
      changes.push({
        path: prevPath,
        operation: 'delete',
      });
    }
  }

  if (changes.length === 0) {
    log.debug('No effective changes for project (all hashes match)', { projectId });
  } else {
    log.debug('Project serialized with hash diff', {
      projectId,
      totalFiles: currentFiles.length,
      changedFiles: changes.length,
    });
  }

  return { changes, currentHashes, currentContents, syncStateKey };
}

/**
 * Serialize a preset from IndexedDB into FileChange[] for Git.
 * Uses SHA-256 hashes to only include files that actually changed.
 */
async function serializePreset(presetId: string): Promise<SerializationResult> {
  const syncStateKey = `gitSync:preset:${presetId}`;

  const preset = await db.presets.get(presetId);
  if (!preset) {
    log.warn('Preset not found for serialization', { presetId });
    return { changes: [], currentHashes: {}, currentContents: new Map(), syncStateKey };
  }

  const presetTemplates = await db.templates.getByPreset(presetId);

  const slug = generateSlug(preset.name);
  const dir = `presets/${preset.id}_${slug}`;

  // KNOWN LIMITATION: If the preset is renamed, the slug changes and the dir
  // changes. Old files under the previous dir are not detected for deletion
  // because we only check `${dir}/` prefix against previousState. A future fix
  // could store the previous dir path in syncState to detect renames and emit
  // delete operations for the old directory's files.

  // Build all current files
  const currentFiles: { path: string; content: string; encoding: 'text' | 'base64' }[] = [];

  // preset.json
  currentFiles.push({
    path: `${dir}/preset.json`,
    content: generatePresetJson(preset),
    encoding: 'text',
  });

  // template files
  for (const tpl of presetTemplates) {
    const filePath = getTemplateGitPath(dir, tpl);
    if (!filePath) continue;

    currentFiles.push({
      path: filePath,
      content: tpl.content,
      encoding: isBinaryTemplate(tpl) ? 'base64' : 'text',
    });
  }

  // Load previous sync state and compute hash diff
  const previousState = await loadSyncState(syncStateKey);
  const currentHashes: Record<string, string> = {};
  const currentContents = new Map<string, string>();
  const changes: FileChange[] = [];

  for (const file of currentFiles) {
    const hash = await computeHash(file.content);
    currentHashes[file.path] = hash;
    currentContents.set(file.path, file.content);

    if (previousState.fileHashes[file.path] !== hash) {
      changes.push({
        path: file.path,
        operation: previousState.fileHashes[file.path] ? 'update' : 'create',
        content: file.content,
        encoding: file.encoding,
      });
    }
  }

  // Detect deleted files
  const dirPrefix = `${dir}/`;
  for (const prevPath of Object.keys(previousState.fileHashes)) {
    if (prevPath.startsWith(dirPrefix) && !currentHashes[prevPath]) {
      changes.push({
        path: prevPath,
        operation: 'delete',
      });
    }
  }

  if (changes.length === 0) {
    log.debug('No effective changes for preset (all hashes match)', { presetId });
  } else {
    log.debug('Preset serialized with hash diff', {
      presetId,
      totalFiles: currentFiles.length,
      changedFiles: changes.length,
    });
  }

  return { changes, currentHashes, currentContents, syncStateKey };
}

// ============================================================================
// SERIALIZATION HELPERS
// ============================================================================

/**
 * Generate manifest.yaml content from project data.
 * Compatible with AutomaticBook format, enriched with Preparo fields.
 */
function generateManifestYaml(
  project: Project,
  documents: Document[],
  projectTemplates: Template[],
  extraYaml?: string,
): string {
  const lines: string[] = [];
  lines.push(`version: 1`);
  lines.push(`lastModified: "${project.updatedAt}"`);
  lines.push('');
  lines.push(`metadata: metadata.yaml`);
  lines.push('');

  // texts (only documents with a defined type)
  lines.push('texts:');
  const filenameResults = generateFilenames(documents);
  for (const { doc, filename, section } of filenameResults) {
    lines.push(`  - file: texts/${filename}`);
    lines.push(`    type: ${section}`);
    lines.push(`    id: "${doc.id}"`);
    lines.push(`    documentType: ${doc.documentType}`);
    lines.push(`    title: "${escapeYamlString(doc.title)}"`);
    lines.push(`    order: ${doc.order ?? 0}`);
    lines.push(`    lastModified: "${doc.updatedAt}"`);
  }

  // assets
  lines.push('');
  lines.push('assets:');
  lines.push('  cover: assets/cover.jpg');

  // config
  const configTemplates = projectTemplates.filter(
    t => isConfigTemplate(t)
  );
  if (configTemplates.length > 0) {
    lines.push('');
    lines.push('config:');
    for (const tpl of configTemplates) {
      const name = tpl.filename.replace(/\.json$/, '');
      lines.push(`  ${name}: config/${tpl.filename}`);
    }
  }

  // template directory pointer (for AutomaticBook compatibility)
  const nonConfigTemplates = projectTemplates.filter(
    t => !isConfigTemplate(t)
  );
  if (nonConfigTemplates.length > 0) {
    lines.push('');
    lines.push('template: template');

    // detailed templates listing
    lines.push('');
    lines.push('templates:');

    const bySection = groupBy(nonConfigTemplates, t => t.templateSection);
    for (const [section, tpls] of Object.entries(bySection)) {
      lines.push(`  ${section}:`);
      for (const tpl of tpls) {
        const filePath = getTemplateRelPath(tpl);
        if (filePath) {
          lines.push(`    - file: ${filePath}`);
          lines.push(`      lastModified: "${tpl.updatedAt}"`);
        }
      }
    }
  }

  // Passthrough: preserve sections from Git that Preparo doesn't own (outputs, buildInfo, etc.)
  // If we have preserved extra YAML from the last hydration, re-inject it.
  // Otherwise, write default empty outputs for new projects.
  if (extraYaml && extraYaml.trim()) {
    lines.push('');
    lines.push(extraYaml);
  } else {
    lines.push('');
    lines.push('outputs:');
    lines.push('  indesign: []');
    lines.push('  epub: []');
    lines.push('  pdf: []');
  }

  return lines.join('\n') + '\n';
}

/**
 * Generate .preparo.json with Preparo-specific metadata.
 */
function generatePreparoJson(project: Project, documents: Document[]): string {
  const data: Record<string, unknown> = {
    id: project.id,
    projectType: project.projectType,
    description: project.description || '',
    author: project.author || '',
    settings: project.settings || '{}',
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    documents: {} as Record<string, unknown>,
  };

  for (const doc of documents) {
    (data.documents as Record<string, unknown>)[doc.id] = {
      status: doc.status,
      wordCount: doc.wordCount ?? 0,
      charCount: doc.charCount ?? 0,
      titleManuallySet: doc.titleManuallySet ?? false,
    };
  }

  return JSON.stringify(data, null, 2) + '\n';
}

/**
 * Generate preset.json from a Preset object.
 */
function generatePresetJson(preset: Preset): string {
  const data: Record<string, unknown> = {
    name: preset.name,
    created: preset.created,
  };

  if (preset.mapping) data.mapping = JSON.parse(preset.mapping);
  if (preset.typography) data.typography = JSON.parse(preset.typography);
  if (preset.pagelayout) data.pagelayout = JSON.parse(preset.pagelayout);
  if (preset.grep) data.grep = JSON.parse(preset.grep);
  if (preset.export) data.export = JSON.parse(preset.export);
  if (preset.general) data.general = JSON.parse(preset.general);
  if (preset.metadata) data.metadata = JSON.parse(preset.metadata);

  return JSON.stringify(data, null, 2) + '\n';
}

/**
 * Build a map from document id → filename using the shared fileNaming
 * module.  This ensures git filenames match exactly what the UI shows.
 */
function buildDocFilenameMap(documents: Document[]): Map<string, string> {
  const results = generateFilenames(documents);
  const map = new Map<string, string>();
  for (const r of results) {
    map.set(r.doc.id, r.filename);
  }
  return map;
}

/**
 * Generate a URL-safe slug from a string (used for preset directory names).
 */
function generateSlug(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}

/**
 * Escape special characters in YAML string values.
 */
function escapeYamlString(str: string): string {
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Check if a template is a config file (stored under config/).
 */
function isConfigTemplate(tpl: Template): boolean {
  return tpl.templateSection === 'config';
}

/**
 * Get the Git path for a template within a project/preset directory.
 */
function getTemplateGitPath(dir: string, tpl: Template): string | null {
  if (isConfigTemplate(tpl)) {
    return `${dir}/config/${tpl.filename}`;
  }
  const relPath = getTemplateRelPath(tpl);
  return relPath ? `${dir}/${relPath}` : null;
}

/**
 * Get the relative path for a template (within template/ directory).
 */
function getTemplateRelPath(tpl: Template): string | null {
  const sectionMap: Record<string, string> = {
    'epub3': 'template/epub/style',
    'epub2': 'template/epub/style',
    'fonts': 'template/epub/fonts',
    'luafilters': 'template/epub/lua',
    'csl': 'template/csl',
    'indesign': 'template/indesign',
    'preparo': 'template/preparo',
  };

  const dir = sectionMap[tpl.templateSection];
  if (!dir) return null;

  return `${dir}/${tpl.filename}`;
}

/**
 * Check if a template should be treated as binary (base64).
 */
function isBinaryTemplate(tpl: Template): boolean {
  const binaryTypes = ['font', 'indesign'];
  const binaryExtensions = ['.ttf', '.otf', '.woff', '.woff2', '.indd', '.indb', '.idml'];

  if (binaryTypes.includes(tpl.templateType)) return true;
  return binaryExtensions.some(ext => tpl.filename.toLowerCase().endsWith(ext));
}

/**
 * Extract base64 data from a data URL (strip the prefix).
 */
function extractBase64FromDataUrl(dataUrl: string): string | null {
  const match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
  return match ? match[1] : null;
}

/**
 * Group an array by a key function.
 */
function groupBy<T>(arr: T[], keyFn: (item: T) => string): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of arr) {
    const key = keyFn(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
  }
  return result;
}

// ============================================================================
// COMMIT MESSAGE GENERATION
// ============================================================================

/**
 * Generate a human-readable commit message from the set of changes.
 *
 * Examples:
 * - feat(projet): modifier "Chapitre 3 — La crise"
 * - feat(projet): mettre à jour les métadonnées
 * - feat(projet): ajouter couverture.jpg
 * - feat(preset): modifier "Rupture" — typography
 * - chore: synchronisation automatique (3 projets)
 */
async function generateCommitMessage(
  projectEntries: Map<string, DirtyEntry>,
  presetIds: Set<string>,
  allChanges: FileChange[],
): Promise<string> {
  const parts: string[] = [];

  // --- Project part ---
  if (projectEntries.size === 1) {
    const [projectId, entry] = [...projectEntries.entries()][0];
    const types = [...entry.changeTypes];

    if (types.length === 1) {
      switch (types[0]) {
        case 'document': {
          parts.push(await describeDocumentChange(projectId, allChanges));
          break;
        }
        case 'metadata':
          parts.push('feat(projet): mettre à jour les métadonnées');
          break;
        case 'image': {
          parts.push(describeImageChange(allChanges));
          break;
        }
        case 'template':
          parts.push(describeTemplateChange(allChanges));
          break;
        case 'config':
          parts.push(describeConfigChange(allChanges));
          break;
        default:
          parts.push('feat(projet): synchronisation');
      }
    } else {
      // Multiple change types in a single project
      const fileCount = allChanges.filter(c => c.path.startsWith(`projects/${projectId}/`)).length;
      parts.push(`feat(projet): synchronisation (${fileCount} fichiers)`);
    }
  } else if (projectEntries.size > 1) {
    parts.push(`chore: synchronisation automatique (${projectEntries.size} projets)`);
  }

  // --- Preset part ---
  if (presetIds.size === 1) {
    const presetId = [...presetIds][0];
    parts.push(await describePresetChange(presetId, allChanges));
  } else if (presetIds.size > 1) {
    parts.push(`chore: synchronisation presets (${presetIds.size})`);
  }

  if (parts.length === 0) {
    return 'chore: sync from Preparo';
  }

  return parts.join(' — ');
}

/**
 * Describe a document change for the commit message.
 * Looks up the document title from IndexedDB when a single document changed.
 */
async function describeDocumentChange(projectId: string, changes: FileChange[]): Promise<string> {
  const docChanges = changes.filter(
    c => c.path.includes('/texts/') && c.operation !== 'delete'
  );

  if (docChanges.length === 1) {
    // Try to find the document title
    const documents = await db.documents.getByProject(projectId);
    const fnMap = buildDocFilenameMap(documents);
    const changedPath = docChanges[0].path;
    const matchedDoc = documents.find(d => {
      const fn = fnMap.get(d.id);
      return fn !== undefined && changedPath.endsWith(`/texts/${fn}`);
    });
    if (matchedDoc) {
      return `feat(projet): modifier "${truncate(matchedDoc.title, 50)}"`;
    }
    // Fallback: extract readable name from path
    return `feat(projet): modifier "${titleFromPath(changedPath)}"`;
  }

  if (docChanges.length > 1) {
    return `feat(projet): modifier ${docChanges.length} documents`;
  }

  // Only deletions or only metadata files changed
  const deletions = changes.filter(c => c.path.includes('/texts/') && c.operation === 'delete');
  if (deletions.length > 0) {
    return `feat(projet): supprimer ${deletions.length} document${deletions.length > 1 ? 's' : ''}`;
  }

  return 'feat(projet): modifier le contenu';
}

/**
 * Describe an image change for the commit message.
 */
function describeImageChange(changes: FileChange[]): string {
  const imgChanges = changes.filter(c => c.path.includes('/assets/'));

  if (imgChanges.length === 1) {
    const filename = imgChanges[0].path.split('/').pop() || '';
    const verb = imgChanges[0].operation === 'delete' ? 'supprimer' : 'ajouter';
    return `feat(projet): ${verb} ${filename}`;
  }

  if (imgChanges.length > 1) {
    return `feat(projet): modifier les images (${imgChanges.length})`;
  }

  return 'feat(projet): modifier les images';
}

/**
 * Describe a template change for the commit message.
 */
function describeTemplateChange(changes: FileChange[]): string {
  const tplChanges = changes.filter(c => c.path.includes('/template/'));

  if (tplChanges.length === 1) {
    const filename = tplChanges[0].path.split('/').pop() || '';
    return `feat(projet): modifier template ${filename}`;
  }

  return 'feat(projet): modifier les templates';
}

/**
 * Describe a config change for the commit message.
 */
function describeConfigChange(changes: FileChange[]): string {
  const cfgChanges = changes.filter(c => c.path.includes('/config/'));

  if (cfgChanges.length === 1) {
    const name = (cfgChanges[0].path.split('/').pop() || '').replace(/\.json$/, '');
    return `feat(projet): modifier config ${name}`;
  }

  return 'feat(projet): modifier la configuration';
}

/**
 * Describe a preset change for the commit message.
 * Looks up the preset name from IndexedDB.
 */
async function describePresetChange(presetId: string, changes: FileChange[]): Promise<string> {
  const preset = await db.presets.get(presetId);
  const presetName = preset?.name || presetId.substring(0, 8);

  // Find which config files changed in this preset
  const presetChanges = changes.filter(c => c.path.startsWith('presets/') && c.path.includes('/config/'));

  if (presetChanges.length === 1) {
    const configName = (presetChanges[0].path.split('/').pop() || '').replace(/\.json$/, '');
    return `feat(preset): modifier "${truncate(presetName, 30)}" — ${configName}`;
  }

  return `feat(preset): modifier "${truncate(presetName, 30)}"`;
}

/**
 * Extract a human-readable title from a document filename path.
 * e.g. "projects/abc/texts/03-chapitre-la-crise.md" → "chapitre la crise"
 */
function titleFromPath(filePath: string): string {
  const filename = filePath.split('/').pop() || '';
  return filename
    .replace(/\.md$/, '')
    .replace(/^\d+-/, '') // strip leading order prefix
    .replace(/-/g, ' ')
    .trim() || filename;
}

/**
 * Truncate a string to a maximum length, adding ellipsis if needed.
 */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.substring(0, maxLen - 1) + '…';
}

// ============================================================================
// QUEUE FLUSH (CORE SYNC LOGIC)
// ============================================================================

/**
 * Flush all pending changes to Git.
 * Coalescences multiple dirty entries into a single atomic commit.
 */
async function flushQueue(): Promise<PushResult> {
  // Guard against concurrent flushes — re-schedule if there are pending changes
  if (isSyncing) {
    if (dirtyQueue.size > 0 || dirtyPresets.size > 0) {
      log.debug('Sync in progress, re-scheduling flush for pending changes');
      if (!structureTimer && !contentTimer) {
        structureTimer = setTimeout(() => {
          structureTimer = null;
          void flushQueue();
        }, sync.debounceStructureMs);
      }
    } else {
      log.debug('Sync already in progress, skipping flush');
    }
    return { success: true, filesChanged: 0 };
  }

  // Check preconditions
  if (!sync.enabled) {
    return { success: false, error: 'Sync is disabled' };
  }

  if (!get(isOnline)) {
    log.debug('Offline — changes will be pushed when back online');
    return { success: false, error: 'Offline' };
  }

  if (dirtyQueue.size === 0 && dirtyPresets.size === 0) {
    return { success: true, filesChanged: 0 };
  }

  const client = getSupabaseClient();
  if (!client) {
    syncStatus.set('disabled');
    return { success: false, error: 'Supabase client not available' };
  }

  const token = await getAccessToken();
  if (!token) {
    log.debug('Not authenticated, skipping sync');
    return { success: false, error: 'Not authenticated' };
  }

  // Snapshot and clear the queue before starting async work
  const projectEntries = new Map(dirtyQueue);
  const presetEntries = new Set(dirtyPresets);
  dirtyQueue.clear();
  dirtyPresets.clear();
  clearAllTimers();

  isSyncing = true;
  syncStatus.set('syncing');

  try {
    // Serialize all dirty data into file changes (with hash diffing)
    const allChanges: FileChange[] = [];
    const serializationResults: SerializationResult[] = [];

    for (const [projectId] of projectEntries) {
      const sr = await serializeProject(projectId);
      allChanges.push(...sr.changes);
      serializationResults.push(sr);
    }

    for (const presetId of presetEntries) {
      const sr = await serializePreset(presetId);
      allChanges.push(...sr.changes);
      serializationResults.push(sr);
    }

    if (allChanges.length === 0) {
      log.debug('No effective changes to push (all hashes match)');
      syncStatus.set('idle');
      // Use live queue size — new entries may have arrived since snapshot
      pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);
      return { success: true, filesChanged: 0 };
    }

    // Generate commit message and push
    const commitMessage = await generateCommitMessage(projectEntries, presetEntries, allChanges);
    const result = await pushChanges(allChanges, commitMessage);

    if (result.success) {
      // Persist updated file hashes for each project/preset, preserving manifestExtras
      // Update baselines for mergeable files to the pushed content (current truth)
      for (const sr of serializationResults) {
        if (sr.syncStateKey) {
          const existingState = await loadSyncState(sr.syncStateKey);
          const updatedBaselines = { ...(existingState.baselineContents ?? {}) };
          for (const change of sr.changes) {
            if (change.content !== undefined &&
                ((change.path.includes('/config/') && change.path.endsWith('.json')) ||
                 change.path.endsWith('/metadata.yaml'))) {
              updatedBaselines[change.path] = change.content;
            }
          }
          // Look up the treeHash for this project from the push response
          const projectId = sr.syncStateKey.replace('gitSync:project:', '').replace('gitSync:preset:', '');
          const treeHash = result.treeHashes?.[projectId] ?? existingState.treeHash ?? null;

          await saveSyncState(sr.syncStateKey, {
            fileHashes: sr.currentHashes,
            lastCommitSha: result.commitSha || null,
            manifestExtras: existingState.manifestExtras,
            baselineContents: updatedBaselines,
            treeHash,
          });
        }
      }

      conflictRetryCount = 0; // Reset on success
      syncStatus.set('idle');
      pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);
      lastSyncedAt.set(new Date().toISOString());
      lastSyncError.set(null);
      log.info('Sync completed', { commitSha: result.commitSha, filesChanged: result.filesChanged });
    } else {
      // Re-queue failed entries
      for (const [id, entry] of projectEntries) {
        if (!dirtyQueue.has(id)) dirtyQueue.set(id, entry);
      }
      for (const id of presetEntries) {
        dirtyPresets.add(id);
      }
      pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);

      if (result.code === 'RATE_LIMITED' && result.retryAfterMs) {
        log.warn('Rate limited, retrying later', { retryAfterMs: result.retryAfterMs });
        setTimeout(() => void flushQueue(), result.retryAfterMs);
        syncStatus.set('idle');
      } else if (result.code === 'CONFLICT') {
        conflictRetryCount++;
        if (conflictRetryCount > MAX_CONFLICT_RETRIES) {
          conflictRetryCount = 0;
          syncStatus.set('error');
          lastSyncError.set('Conflict persists after retries — please reload');
          log.error('Conflict retry limit reached, giving up', {
            retries: MAX_CONFLICT_RETRIES,
          });
        } else {
          const backoffMs = 2_000 * Math.pow(2, conflictRetryCount - 1); // 2s, 4s, 8s
          log.warn('Conflict detected, hydrating then retrying', {
            attempt: conflictRetryCount,
            maxRetries: MAX_CONFLICT_RETRIES,
            backoffMs,
          });
          syncStatus.set('idle');
          // Pull remote changes before retrying push (pull-before-push)
          setTimeout(async () => {
            try {
              const index = await fetchProjectIndex();
              const stale = [...index.entries()]
                .filter(([, e]) => e.hydrationState === 'stale');
              for (const [id] of stale) {
                await hydrateProject(id);
              }
            } catch (err) {
              log.warn('Pre-retry hydration failed', { error: (err as Error).message });
            }
            void flushQueue();
          }, backoffMs);
        }
      } else {
        conflictRetryCount = 0; // Reset on non-conflict errors
        syncStatus.set('error');
        lastSyncError.set(result.error || 'Unknown sync error');
        log.error('Sync failed', { error: result.error, code: result.code });
      }
    }

    return result;
  } catch (error) {
    // Re-queue on unexpected errors
    for (const [id, entry] of projectEntries) {
      if (!dirtyQueue.has(id)) dirtyQueue.set(id, entry);
    }
    for (const id of presetEntries) {
      dirtyPresets.add(id);
    }
    pendingChangesCount.set(dirtyQueue.size + dirtyPresets.size);

    const msg = (error as Error).message;
    syncStatus.set('error');
    lastSyncError.set(msg);
    log.error('Sync error', { error: msg });

    return { success: false, error: msg };
  } finally {
    isSyncing = false;
  }
}

// ============================================================================
// INITIALIZATION & LIFECYCLE
// ============================================================================

/**
 * Initialize the git sync service.
 * Should be called after successful authentication.
 */
export async function init(): Promise<boolean> {
  if (!sync.enabled) {
    syncStatus.set('disabled');
    log.info('Git sync is disabled');
    return false;
  }

  if (!api.proxy.enabled) {
    syncStatus.set('disabled');
    log.info('Git sync requires proxy mode (Supabase auth)');
    return false;
  }

  log.info('Initializing git sync service', {
    debounceContentMs: sync.debounceContentMs,
    debounceStructureMs: sync.debounceStructureMs,
    debounceForceMs: sync.debounceForceMs,
  });

  // Check provisioning status
  try {
    const status = await checkStatus();
    if (!status.provisioned) {
      log.warn('User not provisioned — sync will activate after provisioning');
      syncStatus.set('idle');
      return false;
    }

    syncStatus.set('idle');
    log.info('Git sync service initialized');
    return true;
  } catch (error) {
    log.error('Failed to initialize git sync', { error: (error as Error).message });
    syncStatus.set('error');
    lastSyncError.set((error as Error).message);
    return false;
  }
}

/**
 * Stop the git sync service and clear all timers.
 */
export function destroy(): void {
  clearAllTimers();
  if (offlineDetectionCleanup) {
    offlineDetectionCleanup();
    offlineDetectionCleanup = null;
  }
  isSyncing = false;
  isCheckingRemote = false;
  conflictRetryCount = 0;
  dirtyQueue.clear();
  dirtyPresets.clear();
  pendingChangesCount.set(0);
  syncStatus.set('disabled');
  log.info('Git sync service destroyed');
}

/**
 * Clear all debounce timers.
 */
function clearAllTimers(): void {
  if (contentTimer) {
    clearTimeout(contentTimer);
    contentTimer = null;
  }
  if (structureTimer) {
    clearTimeout(structureTimer);
    structureTimer = null;
  }
  if (forceTimer) {
    clearTimeout(forceTimer);
    forceTimer = null;
  }
}

// ============================================================================
// OFFLINE DETECTION
// ============================================================================

/**
 * Set up browser online/offline event listeners.
 * Updates the isOnline store and toggles syncStatus accordingly.
 */
/** Cleanup function for offline detection listeners, set by setupOfflineDetection */
let offlineDetectionCleanup: (() => void) | null = null;

export function setupOfflineDetection(): void {
  if (typeof window === 'undefined') return;

  // Prevent duplicate listener registration on repeated calls
  if (offlineDetectionCleanup) {
    offlineDetectionCleanup();
    offlineDetectionCleanup = null;
  }

  let offlineSince: number | null = null;

  const handleOnline = () => {
    isOnline.set(true);
    const offlineDuration = offlineSince ? Date.now() - offlineSince : 0;
    offlineSince = null;

    if (get(syncStatus) === 'offline') {
      log.info('Back online — starting reconnection sync', {
        offlineDurationMs: offlineDuration,
        pendingProjects: dirtyQueue.size,
        pendingPresets: dirtyPresets.size,
      });

      // Pull-before-push: hydrate stale projects first, then flush local changes
      void reconcileAfterReconnection(offlineDuration);
    }
  };

  const handleOffline = () => {
    isOnline.set(false);
    offlineSince = Date.now();
    if (get(syncStatus) !== 'disabled') {
      syncStatus.set('offline');
      log.info('Offline — sync paused, changes will queue locally');
    }
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Hydrate on tab focus — detect changes pushed by AutomaticBook or other apps
  let lastVisibilityCheck = Date.now();
  const VISIBILITY_CHECK_INTERVAL = 10_000; // 10 seconds minimum between checks

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    if (!sync.enabled) return;

    const status = get(syncStatus);
    if (status === 'disabled' || status === 'syncing' || status === 'offline') return;

    const now = Date.now();
    if (now - lastVisibilityCheck < VISIBILITY_CHECK_INTERVAL) return;
    lastVisibilityCheck = now;

    // Lightweight check: fetch manifests and hydrate stale projects silently
    void checkForRemoteChanges();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Set initial state
  if (!navigator.onLine && get(syncStatus) !== 'disabled') {
    syncStatus.set('offline');
    offlineSince = Date.now();
  }

  // Store cleanup for destroy()
  offlineDetectionCleanup = () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/** Guard flag to prevent concurrent checkForRemoteChanges calls */
let isCheckingRemote = false;

/**
 * Check for remote changes (e.g., pushed by AutomaticBook) and hydrate stale projects.
 * Called on tab focus to keep Preparo in sync without polling.
 */
async function checkForRemoteChanges(): Promise<void> {
  if (isCheckingRemote) return;
  isCheckingRemote = true;

  try {
    // Flush any unsaved editor content to IndexedDB before hydrating,
    // so the 3-way merge sees the latest local state (not stale IndexedDB data).
    try {
      const { saveCurrentDocument } = await import('@stores/index');
      await saveCurrentDocument({ updateStats: false });
    } catch {
      // No document open or import fails — safe to continue
    }

    const index = await fetchProjectIndex();
    const staleProjects = [...index.entries()]
      .filter(([, entry]) => entry.hydrationState === 'stale');

    if (staleProjects.length === 0) return;

    log.info('Remote changes detected on tab focus', {
      staleCount: staleProjects.length,
      staleIds: staleProjects.map(([id]) => id),
    });

    let hydrated = 0;
    const hydratedIds: string[] = [];
    for (const [id] of staleProjects) {
      const ok = await hydrateProject(id);
      if (ok) {
        hydrated++;
        hydratedIds.push(id);
      }
    }

    // Read actual conflict count from the store
    const conflicts = get(syncConflicts).filter(c => !c.resolved).length;

    lastHydrationResult.set({
      success: true,
      projectsHydrated: hydrated,
      presetsHydrated: 0,
      conflicts,
      errors: [],
    });

    if (hydrated > 0) {
      log.info('Tab focus hydration complete', { hydrated, conflicts });

      // Refresh selectedProject store if it was hydrated in the background,
      // so the UI (and exports) reflect the updated data from Git.
      try {
        const { selectedProject, projects } = await import('@stores/appStore');
        const current = get(selectedProject);
        if (current && hydratedIds.includes(current.id)) {
          const refreshed = await db.projects.get(current.id);
          if (refreshed) {
            selectedProject.set(refreshed);
            projects.update(list => list.map(p => p.id === refreshed.id ? refreshed : p));
            log.info('Refreshed selectedProject after background hydration', { projectId: current.id });
          }
        }
      } catch {
        // Store import or refresh failed — non-critical, user can re-select project
      }
    }
  } catch (error) {
    log.warn('Tab focus remote check failed', { error: (error as Error).message });
  } finally {
    isCheckingRemote = false;
  }
}

/**
 * Reconcile local and remote state after coming back online.
 *
 * Flow:
 * 1. Fetch project index (lightweight manifest comparison)
 * 2. Hydrate any stale projects (pull remote changes)
 * 3. If conflicts detected, notify user (don't auto-push conflicting projects)
 * 4. Flush local changes queue (push)
 */
async function reconcileAfterReconnection(offlineDurationMs: number): Promise<void> {
  // Share the same guard as checkForRemoteChanges to prevent concurrent hydrations
  if (isCheckingRemote) {
    log.debug('Remote check already in progress, skipping reconciliation');
    return;
  }
  isCheckingRemote = true;

  syncStatus.set('syncing');

  const hasPendingChanges = dirtyQueue.size > 0 || dirtyPresets.size > 0;

  try {
    // Step 0: Flush unsaved editor content to IndexedDB before hydrating,
    // so the 3-way merge sees the latest local state (not stale IndexedDB data).
    // Without this, content written offline but not yet persisted to IDB
    // would be silently overwritten by the remote version.
    try {
      const { saveCurrentDocument } = await import('@stores/index');
      await saveCurrentDocument({ updateStats: false });
    } catch {
      // No document open or import fails — safe to continue
    }

    // Step 1: Fetch latest project index (manifests only — lightweight)
    const index = await fetchProjectIndex();

    // Step 2: Identify projects that changed remotely while we were offline
    const staleProjects = [...index.entries()]
      .filter(([, entry]) => entry.hydrationState === 'stale' || entry.hydrationState === 'unknown');

    if (staleProjects.length > 0) {
      log.info('Remote changes detected during offline period', {
        staleCount: staleProjects.length,
        staleIds: staleProjects.map(([id]) => id),
      });

      // Step 3a: Create stubs for remote-only projects + hydrate presets
      const hydrationResult = await hydrateFromGit();

      // Step 3b: Actually hydrate each stale project (pull remote → 3-way merge).
      // hydrateFromGit() only creates stubs for NEW projects; existing projects
      // marked 'stale' need an explicit hydrateProject() call to pull changes.
      let hydrated = 0;
      const hydratedIds: string[] = [];
      for (const [id] of staleProjects) {
        const ok = await hydrateProject(id);
        if (ok) {
          hydrated++;
          hydratedIds.push(id);
        }
      }
      hydrationResult.projectsHydrated += hydrated;

      // Refresh selectedProject store if it was hydrated during reconnection
      if (hydrated > 0) {
        try {
          const { selectedProject, projects } = await import('@stores/appStore');
          const current = get(selectedProject);
          if (current && hydratedIds.includes(current.id)) {
            const refreshed = await db.projects.get(current.id);
            if (refreshed) {
              selectedProject.set(refreshed);
              projects.update(list => list.map(p => p.id === refreshed.id ? refreshed : p));
              log.info('Refreshed selectedProject after reconnection hydration', { projectId: current.id });
            }
          }
        } catch {
          // Non-critical
        }
      }

      if (hydrationResult.conflicts > 0 || get(syncConflicts).some(c => !c.resolved)) {
        // Projects with conflicts should NOT be auto-pushed
        // Remove conflicting projects from the dirty queue
        const conflictedProjectIds = new Set(
          get(syncConflicts)
            .filter(c => !c.resolved)
            .map(c => c.projectId)
        );

        for (const conflictId of conflictedProjectIds) {
          dirtyQueue.delete(conflictId);
        }

        log.warn('Conflicts detected after reconnection — user action needed', {
          conflicts: get(syncConflicts).filter(c => !c.resolved).length,
          conflictedProjects: [...conflictedProjectIds],
        });
      }

      log.info('Hydration after reconnection complete', {
        hydrated: hydrationResult.projectsHydrated,
        conflicts: get(syncConflicts).filter(c => !c.resolved).length,
        errors: hydrationResult.errors.length,
      });
    }

    // Step 4: Flush remaining local changes (push)
    if (dirtyQueue.size > 0 || dirtyPresets.size > 0) {
      await flushQueue();
    } else if (hasPendingChanges) {
      // Had pending changes but they were all in conflicting projects
      syncStatus.set('idle');
      pendingChangesCount.set(0);
    } else {
      syncStatus.set('idle');
    }

    log.info('Reconnection sync complete', {
      offlineDurationMs,
      remoteChanges: staleProjects.length,
      localChangesPushed: hasPendingChanges,
    });
  } catch (error) {
    log.error('Reconnection sync failed', { error: (error as Error).message });
    syncStatus.set('error');
    lastSyncError.set((error as Error).message);

    // Still try to flush local changes even if hydration failed
    if (dirtyQueue.size > 0 || dirtyPresets.size > 0) {
      log.info('Attempting to flush local changes despite hydration failure');
      setTimeout(() => void flushQueue(), 3_000);
    }
  } finally {
    isCheckingRemote = false;
  }
}

// ============================================================================
// PROJECT INDEX — Lazy manifest comparison at startup
// ============================================================================

/**
 * Fetch the project index from Git and compare with local IndexedDB.
 *
 * Only fetches manifests (lightweight), not full project contents.
 * Marks each project as 'synced', 'stale', 'local_only', or 'unknown'.
 *
 * Returns the index so the caller can decide which projects need hydration.
 */
export async function fetchProjectIndex(): Promise<Map<string, ProjectIndexEntry>> {
  log.info('Fetching project index from Git');

  const index = new Map<string, ProjectIndexEntry>();

  try {
    const gitProjects = await listProjects();
    const localProjects = await db.projects.getAll();
    const localMap = new Map(localProjects.map(p => [p.id, p]));

    // Process remote projects
    for (const gp of gitProjects) {
      const local = localMap.get(gp.id);

      // Extract lastModified from manifest YAML
      let remoteLastModified: string | null = null;
      if (gp.manifest) {
        const match = gp.manifest.match(/lastModified:\s*['"]?([^'"\n]+)/);
        remoteLastModified = match ? match[1].trim() : null;
      }

      // Parse .preparo.json for metadata (updatedAt, title, projectType)
      let remoteTitle: string | null = null;
      let remoteProjectType: 'book' | 'article' = 'book';
      if (gp.preparo) {
        try {
          const preparo = JSON.parse(gp.preparo);
          if (!remoteLastModified) {
            remoteLastModified = preparo.updatedAt || null;
          }
          remoteTitle = preparo.title || null;
          remoteProjectType = preparo.projectType || 'book';
        } catch { /* ignore parse errors */ }
      }

      // Prefer title from metadata.yaml (canonical Pandoc format, has the real title)
      const metadataTitle = extractTitleFromMetadata(gp.metadata || null);
      if (metadataTitle) {
        remoteTitle = metadataTitle;
      }

      const localLastModified = local?.updatedAt || null;

      // Determine hydration state
      let hydrationState: ProjectHydrationState;

      if (!local) {
        // Project exists in Git but not locally
        hydrationState = 'stale'; // needs to be pulled
      } else {
        // Compare the aggregate tree hash from the server against the stored one.
        // This covers ALL files (config/*.json, texts/*.md, assets/*, etc.),
        // catching changes pushed by any tool (Preparo, AutomaticBook, manual Git).
        const syncStateKey = `gitSync:project:${gp.id}`;
        const syncState = await loadSyncState(syncStateKey);

        if (Object.keys(syncState.fileHashes).length === 0) {
          // Never synced — needs hydration
          hydrationState = 'stale';
        } else if (gp.treeHash && syncState.treeHash) {
          hydrationState = gp.treeHash !== syncState.treeHash ? 'stale' : 'synced';
        } else {
          // treeHash not available (old server or empty project) — fall back to stale
          hydrationState = 'stale';
        }
      }

      index.set(gp.id, {
        id: gp.id,
        remoteLastModified,
        localLastModified,
        hydrationState,
        title: remoteTitle || `Project ${gp.id.substring(0, 8)}`,
        projectType: remoteProjectType,
        treeHash: gp.treeHash,
      });
    }

    // Mark local-only projects (exist locally but not in Git)
    for (const local of localProjects) {
      if (!index.has(local.id)) {
        index.set(local.id, {
          id: local.id,
          remoteLastModified: null,
          localLastModified: local.updatedAt,
          hydrationState: 'local_only',
        });
      }
    }

    projectIndex.set(index);

    log.info('Project index fetched', {
      total: index.size,
      synced: [...index.values()].filter(e => e.hydrationState === 'synced').length,
      stale: [...index.values()].filter(e => e.hydrationState === 'stale').length,
      localOnly: [...index.values()].filter(e => e.hydrationState === 'local_only').length,
    });

    return index;
  } catch (error) {
    log.error('Failed to fetch project index', { error: (error as Error).message });
    throw error;
  }
}

// ============================================================================
// HYDRATION — Git → IndexedDB (intelligent merge, Phase 2)
// ============================================================================

/** Result of a hydration operation */
export interface HydrationResult {
  success: boolean;
  projectsHydrated: number;
  presetsHydrated: number;
  conflicts: number;
  errors: string[];
}

/** Result of merging a single file */
interface FileMergeResult {
  path: string;
  action: MergeAction;
  /** Whether the file was successfully applied (pull/new_remote) */
  applied: boolean;
}

/**
 * Determine the merge action for a single file using 3-way hash comparison.
 *
 * hashLocal:      SHA-256 of current local content
 * hashRemote:     SHA-256 of content in Git
 * hashLastSynced: SHA-256 at last successful sync (from SyncState)
 */
function determineMergeAction(
  hashLocal: string | null,
  hashRemote: string | null,
  hashLastSynced: string | null,
): MergeAction {
  // Both exist and are identical
  if (hashLocal && hashRemote && hashLocal === hashRemote) return 'identical';

  // New remote file (doesn't exist locally)
  if (!hashLocal && hashRemote) return 'new_remote';

  // New local file (doesn't exist in Git)
  if (hashLocal && !hashRemote) return 'new_local';

  // Both exist but differ — check against last synced baseline
  if (hashLocal && hashRemote) {
    const localChanged = hashLocal !== hashLastSynced;
    const remoteChanged = hashRemote !== hashLastSynced;

    if (localChanged && !remoteChanged) return 'push';   // Only local changed
    if (!localChanged && remoteChanged) return 'pull';    // Only remote changed
    if (localChanged && remoteChanged) return 'conflict'; // Both changed
    // Neither changed (shouldn't happen since hashLocal !== hashRemote, but be safe)
    return 'identical';
  }

  return 'identical';
}

/**
 * 3-way merge for JSON files at the top-level key level.
 *
 * For each key: if only one side changed (vs baseline), take that change.
 * If both sides changed the same key to different values, remote wins (last-write-wins).
 *
 * KNOWN LIMITATION: Merge granularity is top-level keys only. If Preparo modifies
 * a nested property (e.g. typography.rules) and AutomaticBook modifies a sibling
 * (e.g. typography.lang), the merge sees a conflict on the "typography" key and
 * remote wins. In practice, config files are split by domain (mapping.json,
 * typography.json, etc.), so most concurrent edits affect different files entirely.
 * Deep recursive merge could be added later if cross-tool concurrent editing
 * of the same config file becomes a real workflow.
 *
 * Returns the merged JSON string or null if parsing fails.
 */
function mergeJsonThreeWay(
  localContent: string,
  remoteContent: string,
  baseContent: string,
): { merged: string; conflictKeys: string[] } | null {
  try {
    const local = JSON.parse(localContent) as Record<string, unknown>;
    const remote = JSON.parse(remoteContent) as Record<string, unknown>;
    const base = JSON.parse(baseContent) as Record<string, unknown>;

    const allKeys = new Set([
      ...Object.keys(local),
      ...Object.keys(remote),
      ...Object.keys(base),
    ]);

    const merged: Record<string, unknown> = {};
    const conflictKeys: string[] = [];

    // Use a sentinel for undefined values so JSON.stringify comparisons work
    const ABSENT = '__ABSENT__';
    const serialize = (v: unknown): string =>
      v === undefined ? ABSENT : JSON.stringify(v);

    for (const key of allKeys) {
      const localVal = serialize(local[key]);
      const remoteVal = serialize(remote[key]);
      const baseVal = serialize(base[key]);

      if (localVal === remoteVal) {
        // Both agree (including both deleted) — take either
        if (local[key] !== undefined) merged[key] = local[key];
        // If both deleted, key is simply absent from merged
      } else if (localVal === baseVal) {
        // Only remote changed (including remote deletion) — take remote
        if (remote[key] !== undefined) merged[key] = remote[key];
        // If remote deleted, key is absent from merged
      } else if (remoteVal === baseVal) {
        // Only local changed (including local deletion) — keep local
        if (local[key] !== undefined) merged[key] = local[key];
        // If local deleted, key is absent from merged
      } else {
        // Both changed differently — remote wins (last-write-wins)
        conflictKeys.push(key);
        if (remote[key] !== undefined) merged[key] = remote[key];
        // If remote deleted the key, it stays deleted (remote wins)
      }
    }

    return { merged: JSON.stringify(merged, null, 2), conflictKeys };
  } catch {
    return null;
  }
}

/**
 * 3-way merge for metadata.yaml at the top-level key level.
 *
 * Splits YAML into top-level sections (key + indented body) and merges section by section.
 * If both sides changed the same section, remote wins.
 *
 * Returns the merged YAML string or null if parsing fails.
 */
function mergeYamlMetadataThreeWay(
  localContent: string,
  remoteContent: string,
  baseContent: string,
): { merged: string; conflictKeys: string[] } | null {
  try {
    const localSections = splitYamlSections(localContent);
    const remoteSections = splitYamlSections(remoteContent);
    const baseSections = splitYamlSections(baseContent);

    const allKeys = new Set([
      ...localSections.keys(),
      ...remoteSections.keys(),
      ...baseSections.keys(),
    ]);

    let mergedPreamble = '';
    const mergedSections: string[] = [];
    const conflictKeys: string[] = [];

    // Preserve key ordering: use remote order as primary, then local additions
    const orderedKeys: string[] = [];
    for (const key of remoteSections.keys()) {
      if (key !== '__preamble__') orderedKeys.push(key);
    }
    for (const key of localSections.keys()) {
      if (key !== '__preamble__' && !orderedKeys.includes(key)) orderedKeys.push(key);
    }
    // Add any base-only keys (shouldn't happen often, but be safe)
    for (const key of allKeys) {
      if (key !== '__preamble__' && !orderedKeys.includes(key)) orderedKeys.push(key);
    }

    // Handle preamble (comments before first key) — take remote if changed, else local
    const localPreamble = localSections.get('__preamble__') ?? '';
    const remotePreamble = remoteSections.get('__preamble__') ?? '';
    const basePreamble = baseSections.get('__preamble__') ?? '';
    if (localPreamble === remotePreamble) {
      mergedPreamble = localPreamble;
    } else if (localPreamble === basePreamble) {
      mergedPreamble = remotePreamble;
    } else if (remotePreamble === basePreamble) {
      mergedPreamble = localPreamble;
    } else {
      mergedPreamble = remotePreamble; // Both changed — remote wins
    }

    for (const key of orderedKeys) {
      const localVal = localSections.get(key) ?? '';
      const remoteVal = remoteSections.get(key) ?? '';
      const baseVal = baseSections.get(key) ?? '';

      if (localVal === remoteVal) {
        // Both agree (including both deleted) — take either
        if (localVal) mergedSections.push(localVal);
      } else if (localVal === baseVal) {
        // Only remote changed (may be addition, modification, or deletion)
        if (remoteVal) mergedSections.push(remoteVal);
        // If remote deleted (empty), section is omitted
      } else if (remoteVal === baseVal) {
        // Only local changed (may be addition, modification, or deletion)
        if (localVal) mergedSections.push(localVal);
        // If local deleted (empty), section is omitted
      } else {
        // Both changed differently — remote wins (last-write-wins)
        conflictKeys.push(key);
        if (remoteVal) mergedSections.push(remoteVal);
        // If remote deleted the section, it stays deleted (remote wins)
      }
    }

    // Reconstruct with --- delimiters if any version had them
    const hasDelimiters = baseContent.trimStart().startsWith('---') ||
                          localContent.trimStart().startsWith('---') ||
                          remoteContent.trimStart().startsWith('---');

    // Build merged content: preamble + sections with blank line separators
    const parts: string[] = [];
    if (mergedPreamble) parts.push(mergedPreamble);
    parts.push(...mergedSections);
    let merged = parts.join('\n\n');
    if (hasDelimiters && !merged.trimStart().startsWith('---')) {
      merged = '---\n' + merged;
    }
    if (hasDelimiters && !merged.trimEnd().endsWith('---')) {
      merged = merged.trimEnd() + '\n---\n';
    }

    return { merged, conflictKeys };
  } catch {
    return null;
  }
}

/**
 * Split a YAML string into top-level sections.
 * Each section is a top-level key and all its indented content.
 * Returns a Map preserving insertion order.
 *
 * A special '__preamble__' key captures comments/blank lines before the first key.
 * Trailing blank lines are trimmed from each section to prevent whitespace inflation.
 */
function splitYamlSections(yaml: string): Map<string, string> {
  const sections = new Map<string, string>();
  const lines = yaml.split('\n');
  let currentKey = '';
  let currentLines: string[] = [];

  // Helper to flush a section, trimming trailing blank lines
  const flushSection = () => {
    if (!currentKey) return;
    // Trim trailing empty lines to prevent whitespace inflation
    while (currentLines.length > 0 && currentLines[currentLines.length - 1].trim() === '') {
      currentLines.pop();
    }
    sections.set(currentKey, currentLines.join('\n'));
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip --- delimiters
    if (trimmed === '---') continue;

    // Detect top-level key: non-indented, non-empty, non-comment, contains colon
    // Regex supports dotted keys (a.b.c:), quoted keys ("key":, 'key':), and unicode
    const isTopLevelKey = trimmed &&
      !line.startsWith(' ') && !line.startsWith('\t') &&
      !trimmed.startsWith('#') &&
      /^["']?[^\s:]+["']?\s*:/.test(trimmed);

    if (isTopLevelKey) {
      // Flush previous section
      flushSection();
      // Extract key name (strip quotes)
      const keyMatch = trimmed.match(/^["']?([^\s:"']+)["']?\s*:/);
      currentKey = keyMatch ? keyMatch[1] : trimmed.split(':')[0].trim();
      currentLines = [line];
    } else if (currentKey) {
      // Part of current section (indented content, comments, blank lines)
      currentLines.push(line);
    } else {
      // Before first key — capture as preamble (comments, blank lines)
      if (trimmed) {
        if (!sections.has('__preamble__')) {
          currentKey = '__preamble__';
          currentLines = [line];
        } else {
          // Append to existing preamble — shouldn't happen, but be safe
          const existing = sections.get('__preamble__') || '';
          sections.set('__preamble__', existing + '\n' + line);
        }
      }
    }
  }

  // Flush last section
  flushSection();

  return sections;
}

/**
 * Register a conflict — saves both versions and notifies the UI.
 */
function registerConflict(
  projectId: string,
  filePath: string,
  localContent: string,
  remoteContent: string,
  localHash: string,
  remoteHash: string,
  lastSyncedHash: string,
): void {
  const conflict: SyncConflict = {
    id: crypto.randomUUID(),
    projectId,
    filePath,
    localContent,
    remoteContent,
    lastSyncedHash,
    localHash,
    remoteHash,
    detectedAt: new Date().toISOString(),
    resolved: false,
  };

  syncConflicts.update(list => [...list, conflict]);
  log.warn('Conflict detected', { projectId, filePath, localHash, remoteHash });
}

/**
 * Resolve a conflict by applying a resolution.
 *
 * resolution:
 * - 'keep_local': push local version to Git (mark dirty)
 * - 'use_remote': pull remote version into IndexedDB
 * - 'manual': user will merge manually (content provided)
 */
export async function resolveConflict(
  conflictId: string,
  resolution: 'keep_local' | 'use_remote' | 'manual',
  manualContent?: string,
): Promise<boolean> {
  const conflicts = get(syncConflicts);
  const conflict = conflicts.find(c => c.id === conflictId);
  if (!conflict) {
    log.warn('Conflict not found', { conflictId });
    return false;
  }

  try {
    if (resolution === 'use_remote') {
      // Apply the remote content to IndexedDB
      await applyRemoteFile(conflict.projectId, conflict.filePath, conflict.remoteContent);
    } else if (resolution === 'manual' && manualContent !== undefined) {
      // Apply the manually merged content
      await applyRemoteFile(conflict.projectId, conflict.filePath, manualContent);
    }
    // 'keep_local': no change to IndexedDB, mark project as dirty to push

    // Update the last synced hash to whichever content we kept
    const chosenContent = resolution === 'use_remote'
      ? conflict.remoteContent
      : resolution === 'manual' && manualContent !== undefined
        ? manualContent
        : conflict.localContent;
    const newHash = await computeHash(chosenContent);

    const syncStateKey = `gitSync:project:${conflict.projectId}`;
    const state = await loadSyncState(syncStateKey);
    state.fileHashes[conflict.filePath] = newHash;
    await saveSyncState(syncStateKey, state);

    // Mark conflict as resolved
    syncConflicts.update(list =>
      list.map(c => c.id === conflictId ? { ...c, resolved: true } : c)
    );

    // If we kept local, mark project dirty so next flush pushes it
    if (resolution === 'keep_local' || resolution === 'manual') {
      markProjectDirty(conflict.projectId, 'document');
    }

    log.info('Conflict resolved', { conflictId, resolution });
    return true;
  } catch (error) {
    log.error('Failed to resolve conflict', { conflictId, error: (error as Error).message });
    return false;
  }
}

/**
 * Dismiss all resolved conflicts.
 */
export function clearResolvedConflicts(): void {
  syncConflicts.update(list => list.filter(c => !c.resolved));
}

/**
 * Apply a remote file's content to IndexedDB.
 *
 * Handles:
 * - texts/*.md → update document content via ID matching (manifest) or slug fallback
 * - metadata.yaml → update project.metadata + extract title
 * - config/*.json → update template content
 * - .preparo.json → update project settings
 */
async function applyRemoteFile(
  projectId: string,
  filePath: string,
  content: string,
): Promise<void> {
  if (filePath.includes('/texts/') && filePath.endsWith('.md')) {
    const filename = filePath.split('/').pop() || '';
    const docs = await db.documents.getByProject(projectId);

    // Use generateFilenames() — the same function used during serialization —
    // so the filename matching is consistent with what serializeProject produces.
    const filenameResults = generateFilenames(docs);
    const match = filenameResults.find(fr => fr.filename === filename);

    if (match) {
      await db.contents.set(match.doc.id, content);
    } else {
      log.warn('Could not match remote file to local document', { filePath, filename, projectId });
    }
  } else if (filePath.endsWith('/metadata.yaml')) {
    // Update raw metadata AND extract title
    const updates: Record<string, unknown> = { metadata: content };
    const title = extractTitleFromMetadata(content);
    if (title) {
      updates.title = title;
    }
    await db.projects.update(projectId, updates);
  } else if (filePath.includes('/config/') && filePath.endsWith('.json')) {
    // Update config template by filename
    const filename = filePath.split('/').pop() || '';
    const templates = await db.templates.getByProject(projectId);
    const match = templates.find(t => t.filename === filename);
    if (match) {
      await db.templates.update(match.id, { content, size: content.length });
    }
  } else if (filePath.endsWith('/.preparo.json')) {
    // Update project settings from .preparo.json
    try {
      const preparo = JSON.parse(content) as Record<string, unknown>;
      const updates: Record<string, unknown> = {};
      if (preparo.settings) updates.settings = preparo.settings as string;
      if (preparo.description) updates.description = preparo.description as string;
      if (preparo.author) updates.author = preparo.author as string;
      if (Object.keys(updates).length > 0) {
        await db.projects.update(projectId, updates);
      }
    } catch {
      log.warn('Failed to parse .preparo.json during apply', { filePath });
    }
  }
}

/**
 * Hydrate a single project from Git into IndexedDB.
 * Phase 2: intelligent merge with 3-way hash comparison.
 *
 * For new projects (not in IndexedDB): full import (no conflicts possible).
 * For existing projects: per-file 3-way merge based on content hashes.
 */
export async function hydrateProject(projectId: string): Promise<boolean> {
  log.info('Hydrating project from Git', { projectId });

  // Update project index state
  projectIndex.update(idx => {
    const entry = idx.get(projectId);
    if (entry) {
      entry.hydrationState = 'hydrating';
      idx.set(projectId, entry);
    }
    return new Map(idx);
  });

  try {
    const files = await getProject(projectId);
    if (files.length === 0) {
      log.warn('No files found in Git for project', { projectId });
      return false;
    }

    // Parse project metadata from .preparo.json
    const preparoFile = files.find(f => f.path.endsWith('/.preparo.json'));
    const metadataFile = files.find(f => f.path.endsWith('/metadata.yaml'));

    if (!preparoFile) {
      log.warn('No .preparo.json found — cannot hydrate', { projectId });
      return false;
    }

    const preparo = JSON.parse(preparoFile.content) as Record<string, unknown>;
    let existingProject = await db.projects.get(projectId);

    // Detect stub projects: exist in DB but have never been fully hydrated.
    // Stubs are created by hydrateFromGit() for the project list (title + type only).
    // They have no sync state hashes, so the 3-way merge would produce false conflicts.
    // We keep the stub record and let importNewProject overwrite it (safe upsert).
    let isStub = false;
    if (existingProject) {
      const stubCheckKey = `gitSync:project:${projectId}`;
      const stubSyncState = await loadSyncState(stubCheckKey);
      if (Object.keys(stubSyncState.fileHashes).length === 0) {
        log.info('Detected stub project — will overwrite with full import', { projectId });
        isStub = true;
      }
    }

    // ---------- NEW PROJECT or STUB (full import, no conflicts) ----------
    if (!existingProject || isStub) {
      await importNewProject(projectId, files, preparo, metadataFile, isStub);
      await saveHydrationHashes(projectId, files);

      projectIndex.update(idx => {
        const entry = idx.get(projectId);
        if (entry) { entry.hydrationState = 'synced'; idx.set(projectId, entry); }
        return new Map(idx);
      });

      log.info('Project hydrated from Git (full import)', { projectId, files: files.length });
      return true;
    }

    // ---------- EXISTING PROJECT (3-way merge) ----------
    const syncStateKey = `gitSync:project:${projectId}`;
    const syncState = await loadSyncState(syncStateKey);
    const mergeResults: FileMergeResult[] = [];
    let hasConflicts = false;
    // Track content produced by 3-way merges (for correct hash + baseline storage)
    const mergedContents = new Map<string, string>();

    // Compute remote file hashes
    const remoteHashes = new Map<string, string>();
    const remoteContents = new Map<string, string>();
    for (const file of files) {
      const hash = await computeHash(file.content);
      remoteHashes.set(file.path, hash);
      remoteContents.set(file.path, file.content);
    }

    // Serialize current local state to get local file hashes
    // We re-use serializeProject to get the canonical file representation
    let localHashes: Record<string, string>;
    let localContents: Map<string, string>;
    try {
      const sr = await serializeProject(projectId);
      localHashes = sr.currentHashes;
      // Use currentContents (ALL files) instead of sr.changes (only modified files).
      // This ensures every local file is available for conflict detection,
      // avoiding a TOCTOU gap between the two loadSyncState() calls.
      localContents = sr.currentContents;
    } catch {
      // If serialization fails, treat as empty local
      localHashes = {};
      localContents = new Map();
    }

    // Merge all known paths (union of remote + local + lastSynced)
    const allPaths = new Set<string>([
      ...remoteHashes.keys(),
      ...Object.keys(localHashes),
      ...Object.keys(syncState.fileHashes),
    ]);

    for (const path of allPaths) {
      const hashRemote = remoteHashes.get(path) || null;
      const hashLocal = localHashes[path] || null;
      const hashLastSynced = syncState.fileHashes[path] || null;

      const action = determineMergeAction(hashLocal, hashRemote, hashLastSynced);

      switch (action) {
        case 'identical':
          mergeResults.push({ path, action, applied: true });
          break;

        case 'pull':
        case 'new_remote': {
          // Remote changed (or new) — apply to IndexedDB
          const remoteContent = remoteContents.get(path);
          if (remoteContent !== undefined) {
            await applyRemoteFile(projectId, path, remoteContent);
            mergeResults.push({ path, action, applied: true });
          } else {
            mergeResults.push({ path, action, applied: false });
          }
          break;
        }

        case 'push':
        case 'new_local':
          // Local changed — will be pushed on next sync
          markProjectDirty(projectId, 'document');
          mergeResults.push({ path, action, applied: true });
          break;

        case 'conflict': {
          const localContent = localContents.get(path) || '';
          const remoteContent = remoteContents.get(path) || '';
          const baseContent = syncState.baselineContents?.[path] || '';

          // Attempt smart merge for JSON config files and metadata.yaml
          const isConfigJson = path.includes('/config/') && path.endsWith('.json');
          const isMetadataYaml = path.endsWith('/metadata.yaml');

          if (isConfigJson && baseContent) {
            const mergeResult = mergeJsonThreeWay(localContent, remoteContent, baseContent);
            if (mergeResult) {
              await applyRemoteFile(projectId, path, mergeResult.merged);
              markProjectDirty(projectId, 'config');
              // Track merged content for correct hash + baseline (not the remote content)
              mergedContents.set(path, mergeResult.merged);
              if (mergeResult.conflictKeys.length > 0) {
                log.warn('JSON merge: remote wins on conflicting keys', {
                  path, conflictKeys: mergeResult.conflictKeys,
                });
              }
              mergeResults.push({ path, action: 'pull', applied: true });
              break;
            }
          }

          if (isMetadataYaml && baseContent) {
            const mergeResult = mergeYamlMetadataThreeWay(localContent, remoteContent, baseContent);
            if (mergeResult) {
              await applyRemoteFile(projectId, path, mergeResult.merged);
              markProjectDirty(projectId, 'metadata');
              // Track merged content for correct hash + baseline
              mergedContents.set(path, mergeResult.merged);
              if (mergeResult.conflictKeys.length > 0) {
                log.warn('YAML merge: remote wins on conflicting keys', {
                  path, conflictKeys: mergeResult.conflictKeys,
                });
              }
              mergeResults.push({ path, action: 'pull', applied: true });
              break;
            }
          }

          // Fallback for files we can't smart-merge (texts, images, etc.)
          hasConflicts = true;
          registerConflict(
            projectId, path,
            localContent, remoteContent,
            hashLocal!, hashRemote!, hashLastSynced!,
          );
          mergeResults.push({ path, action, applied: false });
          break;
        }
      }
    }

    // Also handle project-level metadata (always pull if changed, rarely conflicts)
    const metadataTitle = extractTitleFromMetadata(metadataFile?.content || null);
    const projectData: Record<string, unknown> = {
      projectType: (preparo.projectType as 'book' | 'article') || existingProject.projectType,
      description: (preparo.description as string) || existingProject.description || '',
      author: (preparo.author as string) || existingProject.author || '',
      metadata: metadataFile?.content || existingProject.metadata || '',
      settings: (preparo.settings as string) || existingProject.settings || '{}',
      updatedAt: new Date().toISOString(),
    };
    if (metadataTitle) {
      projectData.title = metadataTitle;
    }
    await db.projects.update(projectId, projectData);

    // Handle new remote documents that don't exist locally
    const textFiles = files.filter(f => f.path.includes('/texts/') && f.path.endsWith('.md'));
    const existingDocs = await db.documents.getByProject(projectId);
    const data = parseProjectData(projectId, files);
    const docIdMap = buildDocIdMap(data.manifest, data.preparoJson);

    for (const textFile of textFiles) {
      const filename = textFile.path.split('/').pop() || '';
      const mergeResult = mergeResults.find(r => r.path === textFile.path);

      // Only create new document records for genuinely new remote files
      if (mergeResult?.action === 'new_remote') {
        const manifestEntry = data.manifest.texts.find(m => m.file === `texts/${filename}`);
        const docMapping = docIdMap.get(filename);
        const docMeta = docMapping?.meta || {};

        // Check no existing doc matches this already (by ID or slug)
        const alreadyExists = existingDocs.some(d => {
          if (docMapping?.id && d.id === docMapping.id) return true;
          const slug = (d.title || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          return filename.includes(slug);
        });

        if (!alreadyExists) {
          await db.documents.add({
            projectId,
            title: manifestEntry?.title || titleFromPath(textFile.path),
            filename,
            status: (docMeta.status as 'todo' | 'in-progress' | 'done') || 'todo',
            documentSection: (manifestEntry?.type as 'frontmatter' | 'bodymatter' | 'backmatter') || 'bodymatter',
            documentType: (manifestEntry?.documentType as string) || null,
            order: manifestEntry?.order ?? 0,
            wordCount: (docMeta.wordCount as number) || 0,
            charCount: (docMeta.charCount as number) || 0,
            titleManuallySet: (docMeta.titleManuallySet as boolean) || false,
            content: textFile.content,
          } as unknown as Parameters<typeof db.documents.add>[0]);
        }
      }
    }

    // Handle new remote images and templates (overwrite strategy for non-text files)
    await syncNonTextFiles(projectId, files, mergeResults);

    // Update sync state with current hashes (for files that were successfully merged)
    const newHashes: Record<string, string> = { ...syncState.fileHashes };
    for (const result of mergeResults) {
      if (result.applied && result.action !== 'conflict') {
        // For 3-way merged files, use the merged content hash (not the remote hash)
        const mergedContent = mergedContents.get(result.path);
        if (mergedContent !== undefined) {
          newHashes[result.path] = await computeHash(mergedContent);
        } else {
          const remoteHash = remoteHashes.get(result.path);
          if (remoteHash) {
            newHashes[result.path] = remoteHash;
          }
        }
      }
    }

    // Preserve manifest extras (outputs, buildInfo) from remote manifest for passthrough
    const manifestExtraYaml = data.manifest.extraYaml || syncState.manifestExtras;

    // Save baseline contents for mergeable files (config/*.json, metadata.yaml)
    // Use merged content as baseline when available (not remote content)
    const newBaselines: Record<string, string> = { ...(syncState.baselineContents ?? {}) };
    for (const file of files) {
      if ((file.path.includes('/config/') && file.path.endsWith('.json')) ||
          file.path.endsWith('/metadata.yaml')) {
        const mergedContent = mergedContents.get(file.path);
        newBaselines[file.path] = mergedContent !== undefined ? mergedContent : file.content;
      }
    }

    // Save the treeHash from the project index so the next fetchProjectIndex
    // can compare it and skip hydration when nothing changed.
    const indexEntry = get(projectIndex).get(projectId);
    await saveSyncState(syncStateKey, {
      fileHashes: newHashes,
      lastCommitSha: syncState.lastCommitSha,
      manifestExtras: manifestExtraYaml,
      baselineContents: newBaselines,
      treeHash: indexEntry?.treeHash ?? null,
    });

    // Update project index
    projectIndex.update(idx => {
      const entry = idx.get(projectId);
      if (entry) {
        entry.hydrationState = hasConflicts ? 'conflict' : 'synced';
        idx.set(projectId, entry);
      }
      return new Map(idx);
    });

    const stats = {
      identical: mergeResults.filter(r => r.action === 'identical').length,
      pulled: mergeResults.filter(r => r.action === 'pull' || r.action === 'new_remote').length,
      pushed: mergeResults.filter(r => r.action === 'push' || r.action === 'new_local').length,
      conflicts: mergeResults.filter(r => r.action === 'conflict').length,
    };
    log.info('Project hydration complete', { projectId, ...stats });

    return true;
  } catch (error) {
    log.error('Failed to hydrate project', { projectId, error: (error as Error).message });

    projectIndex.update(idx => {
      const entry = idx.get(projectId);
      if (entry) { entry.hydrationState = 'stale'; idx.set(projectId, entry); }
      return new Map(idx);
    });

    return false;
  }
}

/**
 * Import a brand-new project from Git (no local state, no conflicts possible).
 * Uses parseProjectData() for structured deserialization.
 */
async function importNewProject(
  projectId: string,
  files: GitProjectFile[],
  preparo: Record<string, unknown>,
  metadataFile: GitProjectFile | undefined,
  replaceStub = false,
): Promise<void> {
  const data = parseProjectData(projectId, files);
  const docIdMap = buildDocIdMap(data.manifest, data.preparoJson);

  // Extract title from metadata.yaml, fallback to .preparo.json
  const title = extractTitleFromMetadata(data.metadataYaml)
    || (preparo.title as string)
    || (preparo.description as string)
    || `Project ${projectId.substring(0, 8)}`;

  // Create or replace project record.
  // When replacing a stub, use update (safe upsert) so we never lose the record
  // if a later step fails. When creating fresh, use add.
  const projectFields = {
    title,
    projectType: (preparo.projectType as 'book' | 'article') || 'book',
    description: (preparo.description as string) || '',
    author: (preparo.author as string) || '',
    metadata: data.metadataYaml || '',
    settings: (preparo.settings as string) || '{}',
  };

  if (replaceStub) {
    await db.projects.update(projectId, projectFields);
  } else {
    await db.projects.add({
      id: projectId,
      ...projectFields,
    } as unknown as Parameters<typeof db.projects.add>[0]);
  }

  // Documents — use stable IDs from manifest/.preparo.json
  for (const [textIdx, manifestEntry] of data.manifest.texts.entries()) {
    const filename = manifestEntry.file.replace(/^texts\//, '');
    const textData = data.texts.get(filename);
    if (!textData) continue;

    // Use ID from docIdMap (stable), fallback to manifest ID, then generate
    const docMapping = docIdMap.get(filename);
    const docId = docMapping?.id || manifestEntry.id || crypto.randomUUID();
    const docMeta = docMapping?.meta || {};

    await db.documents.add({
      projectId,
      title: manifestEntry.title || titleFromPath(textData.path),
      filename,
      status: (docMeta.status as 'todo' | 'in-progress' | 'done') || 'todo',
      documentSection: (manifestEntry.type as 'frontmatter' | 'bodymatter' | 'backmatter') || 'bodymatter',
      documentType: manifestEntry.documentType || null,
      order: manifestEntry.order ?? textIdx,
      wordCount: (docMeta.wordCount as number) || 0,
      charCount: (docMeta.charCount as number) || 0,
      titleManuallySet: (docMeta.titleManuallySet as boolean) || false,
      content: textData.content,
    } as unknown as Parameters<typeof db.documents.add>[0]);
  }

  // Images
  for (const [filename, imgData] of data.images) {
    const mimeType = guessMimeType(filename);
    const coverFilename = data.manifest.assets.cover?.split('/').pop() || '';
    const isCover = filename === coverFilename || filename.toLowerCase().includes('cover');

    await db.images.add({
      projectId,
      filename,
      mimeType,
      dataUrl: imgData.encoding === 'base64'
        ? `data:${mimeType};base64,${imgData.content}`
        : imgData.content,
      size: imgData.size,
      imageSection: isCover ? 'cover' : 'images',
    });
  }

  // Config files (as templates with proper naming)
  for (const [filename, cfgData] of data.configs) {
    await db.templates.add({
      projectId, filename,
      templateType: 'config', templateSection: 'config',
      mimeType: 'application/json', content: cfgData.content,
      size: cfgData.content.length,
    });
  }

  // Template files
  for (const [, tplData] of data.templates) {
    const filename = tplData.path.split('/').pop() || '';
    const { templateType, templateSection } = guessTemplateType(tplData.path);
    await db.templates.add({
      projectId, filename, templateType, templateSection,
      mimeType: guessMimeType(filename), content: tplData.content,
      size: tplData.content.length,
    });
  }
}

/**
 * Sync non-text files (images, configs, templates) — these use overwrite strategy.
 * Text content conflicts are handled separately by the 3-way merge.
 */
async function syncNonTextFiles(
  projectId: string,
  files: GitProjectFile[],
  mergeResults: FileMergeResult[],
): Promise<void> {
  // Only process files that need pulling (new_remote or pull)
  const pullPaths = new Set(
    mergeResults
      .filter(r => (r.action === 'pull' || r.action === 'new_remote') && !r.path.includes('/texts/'))
      .map(r => r.path)
  );

  if (pullPaths.size === 0) return;

  // Pre-fetch existing data once (instead of re-querying inside each loop iteration)
  const [existingImages, existingTemplates] = await Promise.all([
    db.images.getByProject(projectId),
    db.templates.getByProject(projectId),
  ]);
  const imagesByFilename = new Map(existingImages.map(i => [i.filename, i]));
  const templatesByFilename = new Map(existingTemplates.map(t => [t.filename, t]));

  // Images
  const imageFiles = files.filter(f => pullPaths.has(f.path) && f.path.includes('/assets/'));
  for (const imgFile of imageFiles) {
    const filename = imgFile.path.split('/').pop() || '';
    const mimeType = guessMimeType(filename);

    const match = imagesByFilename.get(filename);
    if (match) {
      await db.images.update(match.id, {
        dataUrl: `data:${mimeType};base64,${imgFile.content}`,
        size: imgFile.size,
      });
    } else {
      const added = await db.images.add({
        projectId, filename, mimeType,
        dataUrl: `data:${mimeType};base64,${imgFile.content}`,
        size: imgFile.size,
        imageSection: filename.toLowerCase().includes('cover') ? 'cover' : 'images',
      });
      imagesByFilename.set(filename, added);
    }
  }

  // Config files
  const configFiles = files.filter(f => pullPaths.has(f.path) && f.path.includes('/config/'));
  for (const cfgFile of configFiles) {
    const filename = cfgFile.path.split('/').pop() || '';
    const match = templatesByFilename.get(filename);
    if (match) {
      await db.templates.update(match.id, { content: cfgFile.content, size: cfgFile.size });
    } else {
      const added = await db.templates.add({
        projectId, filename,
        templateType: 'config', templateSection: 'config',
        mimeType: 'application/json', content: cfgFile.content, size: cfgFile.size,
      });
      templatesByFilename.set(filename, added);
    }
  }

  // Template files
  const templateFiles = files.filter(f => pullPaths.has(f.path) && f.path.includes('/template/'));
  for (const tplFile of templateFiles) {
    const filename = tplFile.path.split('/').pop() || '';
    const { templateType, templateSection } = guessTemplateType(tplFile.path);
    const match = templatesByFilename.get(filename);
    if (match) {
      await db.templates.update(match.id, { content: tplFile.content, size: tplFile.size });
    } else {
      const added = await db.templates.add({
        projectId, filename, templateType, templateSection,
        mimeType: guessMimeType(filename), content: tplFile.content, size: tplFile.size,
      });
      templatesByFilename.set(filename, added);
    }
  }
}

/**
 * Save hydration hashes for a newly imported project.
 */
async function saveHydrationHashes(projectId: string, files: GitProjectFile[]): Promise<void> {
  const syncStateKey = `gitSync:project:${projectId}`;
  const fileHashes: Record<string, string> = {};
  for (const file of files) {
    fileHashes[file.path] = await computeHash(file.content);
  }

  // Parse manifest to capture extras (outputs, buildInfo from AutomaticBook)
  const manifestFile = files.find(f => f.path.endsWith('/manifest.yaml'));
  const manifest = manifestFile ? parseManifest(manifestFile.content) : null;

  // Save baseline contents for mergeable files (config/*.json, metadata.yaml)
  const baselineContents: Record<string, string> = {};
  for (const file of files) {
    if ((file.path.includes('/config/') && file.path.endsWith('.json')) ||
        file.path.endsWith('/metadata.yaml')) {
      baselineContents[file.path] = file.content;
    }
  }

  const indexEntry = get(projectIndex).get(projectId);
  await saveSyncState(syncStateKey, {
    fileHashes,
    lastCommitSha: null,
    manifestExtras: manifest?.extraYaml || '',
    baselineContents,
    treeHash: indexEntry?.treeHash ?? null,
  });
}

/**
 * Hydrate a single preset from Git into IndexedDB.
 * Creates the Preset record + associated Template records.
 */
async function hydratePreset(
  dirName: string,
  presetJson: string | null,
): Promise<boolean> {
  // Extract presetId from dirName format: "{uuid}_{slug}"
  const presetId = dirName.split('_')[0];
  if (!presetId) {
    log.warn('Cannot extract presetId from dirName', { dirName });
    return false;
  }

  // Skip if already exists locally
  const existing = await db.presets.get(presetId);
  if (existing) {
    log.debug('Preset already exists locally, skipping', { presetId });
    return false;
  }

  try {
    // Parse preset.json for metadata + inline configs
    let presetData: Record<string, unknown> = {};
    if (presetJson) {
      presetData = JSON.parse(presetJson);
    }

    // Build the Preset record with the same ID from Git (stable across sync)
    const now = new Date().toISOString();
    const preset: Preset = {
      id: presetId,
      name: (presetData.name as string) || dirName,
      created: (presetData.created as string) || now,
      updatedAt: now,
      mapping: presetData.mapping ? JSON.stringify(presetData.mapping) : undefined,
      typography: presetData.typography ? JSON.stringify(presetData.typography) : undefined,
      pagelayout: presetData.pagelayout ? JSON.stringify(presetData.pagelayout) : undefined,
      grep: presetData.grep ? JSON.stringify(presetData.grep) : undefined,
      export: presetData.export ? JSON.stringify(presetData.export) : undefined,
      general: presetData.general ? JSON.stringify(presetData.general) : undefined,
      metadata: presetData.metadata ? JSON.stringify(presetData.metadata) : undefined,
    };

    // Write preset directly (bypass add() which generates a new ID)
    await db.transaction('presets', 'readwrite', (store) => store.put(preset));

    // Fetch all files to get templates
    const files = await getPreset(dirName);

    // Create config templates from config/*.json files
    for (const file of files) {
      if (file.path.includes('/config/') && file.path.endsWith('.json')) {
        const filename = file.path.split('/').pop() || '';
        await db.templates.add({
          projectId: '',
          presetId,
          filename,
          templateType: 'config',
          templateSection: 'config',
          mimeType: 'application/json',
          content: file.content,
          size: file.content.length,
        });
      }
    }

    // Create non-config templates from template/**/* files
    for (const file of files) {
      if (!file.path.includes('/template/')) continue;
      const filename = file.path.split('/').pop() || '';
      if (!filename) continue;

      const { templateType, templateSection } = guessTemplateType(file.path);
      await db.templates.add({
        projectId: '',
        presetId,
        filename,
        templateType,
        templateSection,
        mimeType: guessMimeType(filename),
        content: file.content,
        size: file.content.length,
      });
    }

    // Save sync state hashes so this preset isn't re-pushed
    const syncStateKey = `gitSync:preset:${presetId}`;
    const fileHashes: Record<string, string> = {};
    for (const file of files) {
      fileHashes[file.path] = await computeHash(file.content);
    }
    await saveSyncState(syncStateKey, { fileHashes, lastCommitSha: null });

    log.info('Preset hydrated from Git', { presetId, name: preset.name, files: files.length });
    return true;
  } catch (error) {
    log.error('Failed to hydrate preset', { dirName, error: (error as Error).message });
    return false;
  }
}

/**
 * Fetch project index and create lightweight stub records for remote-only projects,
 * then eagerly hydrate presets (which are useful immediately).
 *
 * Projects are NOT eagerly hydrated — they are hydrated on demand when the user
 * clicks on them (via selectProject → hydrateProject).
 */
export async function hydrateFromGit(): Promise<HydrationResult> {
  const result: HydrationResult = {
    success: true,
    projectsHydrated: 0,
    presetsHydrated: 0,
    conflicts: 0,
    errors: [],
  };

  if (!sync.enabled || !api.proxy.enabled) {
    return result;
  }

  try {
    // --- Projects: create stub records for remote-only projects ---
    const index = await fetchProjectIndex();

    for (const [id, entry] of index) {
      if (entry.hydrationState === 'stale' || entry.hydrationState === 'unknown') {
        // Create a lightweight stub in IndexedDB so the project appears in the list.
        // Full content (documents, images, configs, templates) is loaded on demand
        // when the user clicks on the project (lazy hydration in selectProject).
        const existing = await db.projects.get(id);
        if (!existing) {
          try {
            await db.projects.add({
              id,
              title: entry.title || `Project ${id.substring(0, 8)}`,
              projectType: entry.projectType || 'book',
              description: '',
              author: '',
              metadata: '',
              settings: '{}',
            } as unknown as Parameters<typeof db.projects.add>[0]);
            result.projectsHydrated++;
          } catch (err) {
            log.warn('Failed to create stub project', { id, error: (err as Error).message });
          }
        }
      }
    }

    // --- Presets: eagerly hydrate (they're useful immediately) ---
    const remotePresets = await listPresets();

    for (const rp of remotePresets) {
      const ok = await hydratePreset(rp.dirName, rp.presetJson);
      if (ok) {
        result.presetsHydrated++;
      }
    }

    log.info('Hydration check complete', {
      projects: { total: index.size, stubs: result.projectsHydrated },
      presets: { total: remotePresets.length, hydrated: result.presetsHydrated },
    });
  } catch (error) {
    result.success = false;
    result.errors.push((error as Error).message);
    log.error('Hydration failed', { error: (error as Error).message });
  }

  // Notify subscribers (UI can react to refresh stores)
  lastHydrationResult.set(result);

  return result;
}

// ============================================================================
// DESERIALIZATION — Git → structured data (Phase 2.2)
// ============================================================================

/** Fully parsed manifest.yaml */
export interface ParsedManifest {
  version: number;
  lastModified: string | null;
  texts: ManifestTextEntry[];
  config: Record<string, string>;  // e.g. { mapping: "config/mapping.json" }
  template: string | null;         // e.g. "presets/rupture/template"
  assets: { cover: string | null };
  /** Raw YAML of sections Preparo doesn't own (outputs, buildInfo, etc.) — preserved for passthrough */
  extraYaml: string;
}

/** A single text entry from the manifest */
export interface ManifestTextEntry {
  file: string;
  type: string;                    // frontmatter, bodymatter, backmatter
  lastModified: string | null;
  id: string;
  title: string;
  documentType: string | null;
  order: number;
}

/** Structured project data parsed from Git files */
export interface GitProjectData {
  projectId: string;
  manifest: ParsedManifest;
  metadataYaml: string | null;
  preparoJson: Record<string, unknown>;
  texts: Map<string, { path: string; content: string }>;
  configs: Map<string, { path: string; content: string }>;
  templates: Map<string, { path: string; content: string; encoding: string }>;
  images: Map<string, { path: string; content: string; encoding: string; size: number }>;
}

/**
 * Parse raw Git files into structured GitProjectData.
 * This is the single entry point for deserialization.
 */
export function parseProjectData(projectId: string, files: GitProjectFile[]): GitProjectData {
  const preparoFile = files.find(f => f.path.endsWith('/.preparo.json'));
  const manifestFile = files.find(f => f.path.endsWith('/manifest.yaml'));
  const metadataFile = files.find(f => f.path.endsWith('/metadata.yaml'));

  const preparoJson = preparoFile
    ? JSON.parse(preparoFile.content) as Record<string, unknown>
    : {};

  const manifest = manifestFile
    ? parseManifest(manifestFile.content)
    : { version: 1, lastModified: null, texts: [], config: {}, template: null, assets: { cover: null }, extraYaml: '' };

  const texts = new Map<string, { path: string; content: string }>();
  const configs = new Map<string, { path: string; content: string }>();
  const templates = new Map<string, { path: string; content: string; encoding: string }>();
  const images = new Map<string, { path: string; content: string; encoding: string; size: number }>();

  for (const file of files) {
    const filename = file.path.split('/').pop() || '';

    if (file.path.includes('/texts/') && file.path.endsWith('.md')) {
      texts.set(filename, { path: file.path, content: file.content });
    } else if (file.path.includes('/config/') && file.path.endsWith('.json')) {
      configs.set(filename, { path: file.path, content: file.content });
    } else if (file.path.includes('/template/')) {
      templates.set(file.path, { path: file.path, content: file.content, encoding: file.encoding });
    } else if (file.path.includes('/assets/')) {
      images.set(filename, { path: file.path, content: file.content, encoding: file.encoding, size: file.size });
    }
  }

  return {
    projectId,
    manifest,
    metadataYaml: metadataFile?.content || null,
    preparoJson,
    texts,
    configs,
    templates,
    images,
  };
}

/**
 * Parse a manifest.yaml string into a structured ParsedManifest.
 * Handles the full manifest structure: version, lastModified, texts, config, assets, template.
 */
function parseManifest(yaml: string): ParsedManifest {
  const result: ParsedManifest = {
    version: 1,
    lastModified: null,
    texts: [],
    config: {},
    template: null,
    assets: { cover: null },
    extraYaml: '',
  };

  // Sections that Preparo owns and parses explicitly
  const knownSections = new Set(['version', 'lastModified', 'metadata', 'texts', 'config', 'assets', 'template', 'templates']);

  const lines = yaml.split('\n');
  let section: 'root' | 'texts' | 'config' | 'assets' | 'templates' | 'unknown' = 'root';
  let currentTextEntry: Partial<ManifestTextEntry> = {};
  let textOrder = 0;
  const extraLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    // Detect top-level keys (no indentation)
    if (!line.startsWith(' ') && !line.startsWith('\t') && !line.startsWith('-')) {
      // Flush pending text entry
      if (section === 'texts' && currentTextEntry.file) {
        result.texts.push(finalizeTextEntry(currentTextEntry, textOrder++));
        currentTextEntry = {};
      }

      const keyMatch = trimmed.match(/^(\w+):/);
      const key = keyMatch ? keyMatch[1] : '';

      if (key === 'version') {
        result.version = parseInt(trimmed.replace('version:', '').trim(), 10) || 1;
        section = 'root';
      } else if (key === 'lastModified') {
        result.lastModified = trimmed.replace('lastModified:', '').trim().replace(/['"]/g, '');
        section = 'root';
      } else if (trimmed === 'texts:') {
        section = 'texts';
      } else if (trimmed === 'config:') {
        section = 'config';
      } else if (trimmed === 'assets:') {
        section = 'assets';
      } else if (key === 'template') {
        result.template = trimmed.replace('template:', '').trim();
        section = 'root';
      } else if (trimmed === 'templates:') {
        section = 'templates';
      } else if (key === 'metadata') {
        // Known but simple — skip
        section = 'root';
      } else if (!knownSections.has(key)) {
        // Unknown top-level section (outputs, buildInfo, etc.) — capture as extra YAML
        section = 'unknown';
        extraLines.push(line);
      } else {
        section = 'root';
      }
      continue;
    }

    // Capture indented lines of unknown sections
    if (section === 'unknown') {
      extraLines.push(line);
      continue;
    }

    // Parse section content
    switch (section) {
      case 'texts':
        if (trimmed.startsWith('- file:')) {
          // Flush previous text entry
          if (currentTextEntry.file) {
            result.texts.push(finalizeTextEntry(currentTextEntry, textOrder++));
          }
          currentTextEntry = { file: trimmed.replace('- file:', '').trim() };
        } else if (trimmed.startsWith('type:')) {
          currentTextEntry.type = trimmed.replace('type:', '').trim();
        } else if (trimmed.startsWith('id:')) {
          currentTextEntry.id = trimmed.replace('id:', '').trim().replace(/['"]/g, '');
        } else if (trimmed.startsWith('title:')) {
          currentTextEntry.title = trimmed.replace('title:', '').trim().replace(/['"]/g, '');
        } else if (trimmed.startsWith('documentType:')) {
          currentTextEntry.documentType = trimmed.replace('documentType:', '').trim();
        } else if (trimmed.startsWith('order:')) {
          currentTextEntry.order = parseInt(trimmed.replace('order:', '').trim(), 10);
        } else if (trimmed.startsWith('lastModified:')) {
          currentTextEntry.lastModified = trimmed.replace('lastModified:', '').trim().replace(/['"]/g, '');
        }
        break;

      case 'config': {
        const configMatch = trimmed.match(/^(\w+):\s*(.+)/);
        if (configMatch) {
          result.config[configMatch[1]] = configMatch[2].trim();
        }
        break;
      }

      case 'assets': {
        const assetMatch = trimmed.match(/^cover:\s*(.+)/);
        if (assetMatch) {
          result.assets.cover = assetMatch[1].trim();
        }
        break;
      }
    }
  }

  // Flush last text entry
  if (currentTextEntry.file) {
    result.texts.push(finalizeTextEntry(currentTextEntry, textOrder));
  }

  // Store captured extra sections (outputs, buildInfo, etc.)
  result.extraYaml = extraLines.length > 0 ? extraLines.join('\n') : '';

  return result;
}

/**
 * Finalize a partial ManifestTextEntry with defaults.
 */
function finalizeTextEntry(partial: Partial<ManifestTextEntry>, defaultOrder: number): ManifestTextEntry {
  return {
    file: partial.file || '',
    type: partial.type || 'bodymatter',
    lastModified: partial.lastModified || null,
    id: partial.id || '',
    title: partial.title || '',
    documentType: partial.documentType || null,
    order: partial.order ?? defaultOrder,
  };
}

/**
 * Extract the main title from a metadata.yaml string.
 * Handles both simple `title: "Text"` and structured `title: [{text, type}]` formats.
 * Supports either field order (text before type, or type before text).
 */
function extractTitleFromMetadata(metadataYaml: string | null): string | null {
  if (!metadataYaml) return null;

  // Extract the title block: everything between "title:" and the next top-level key
  const titleBlockMatch = metadataYaml.match(/^title:\s*\n((?:[ \t-].*\n?)*)/m);

  if (titleBlockMatch) {
    const block = titleBlockMatch[1];

    // Split into list items (each starting with "- ")
    const items = block.split(/^(?=-\s)/m).filter(s => s.trim());

    // First pass: find the item with type: main and extract its text
    for (const item of items) {
      if (/type:\s*main/.test(item)) {
        const textMatch = item.match(/text:\s*['"]?(.+?)['"]?\s*$/m);
        if (textMatch) return textMatch[1].trim();
      }
    }

    // Second pass: no type: main found — use the first text entry
    for (const item of items) {
      const textMatch = item.match(/text:\s*['"]?(.+?)['"]?\s*$/m);
      if (textMatch) return textMatch[1].trim();
    }
  }

  // Try flat format: title: "..." or title: '...'
  const flatQuoted = metadataYaml.match(/^title:\s*(['"])(.+?)\1\s*$/m);
  if (flatQuoted) {
    return flatQuoted[2].trim();
  }

  // Flat without quotes: title: Some Text (but NOT title: followed by a list)
  const flatPlain = metadataYaml.match(/^title:\s+([^\s\-'"].+?)\s*$/m);
  if (flatPlain) {
    return flatPlain[1].trim();
  }

  return null;
}

/**
 * Build a document ID map from .preparo.json.
 *
 * Maps filename patterns → document IDs for stable ID hydration.
 * The .preparo.json stores { documents: { [docId]: { status, wordCount, ... } } }
 * and the manifest stores { texts: [{ file, id }] }.
 */
function buildDocIdMap(
  manifest: ParsedManifest,
  preparoJson: Record<string, unknown>,
): Map<string, { id: string; meta: Record<string, unknown> }> {
  const map = new Map<string, { id: string; meta: Record<string, unknown> }>();
  const docsMetadata = (preparoJson.documents || {}) as Record<string, Record<string, unknown>>;

  for (const entry of manifest.texts) {
    const filename = entry.file.replace(/^texts\//, '');
    const docId = entry.id || '';

    // If manifest has an ID, use it; metadata from .preparo.json if available
    if (docId) {
      map.set(filename, {
        id: docId,
        meta: docsMetadata[docId] || {},
      });
    }
  }

  return map;
}

// Legacy wrapper for backward compatibility with 3-way merge code
function parseManifestTexts(yaml: string): ManifestTextEntry[] {
  return parseManifest(yaml).texts;
}

/**
 * Guess MIME type from filename extension.
 */
function guessMimeType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop() || '';
  const mimeMap: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',
    'css': 'text/css',
    'json': 'application/json',
    'yaml': 'text/yaml',
    'yml': 'text/yaml',
    'md': 'text/markdown',
    'ttf': 'font/ttf',
    'otf': 'font/otf',
    'woff': 'font/woff',
    'woff2': 'font/woff2',
    'lua': 'text/x-lua',
    'csl': 'application/xml',
    'indd': 'application/x-indesign',
    'idml': 'application/x-indesign',
  };
  return mimeMap[ext] || 'application/octet-stream';
}

/**
 * Guess template type and section from a Git file path.
 */
function guessTemplateType(filePath: string): {
  templateType: TemplateType;
  templateSection: TemplateSection;
} {
  if (filePath.includes('/template/epub/style')) return { templateType: 'css', templateSection: 'epub3' };
  if (filePath.includes('/template/epub/fonts')) return { templateType: 'font', templateSection: 'fonts' };
  if (filePath.includes('/template/epub/lua')) return { templateType: 'lua', templateSection: 'luafilters' };
  if (filePath.includes('/template/csl')) return { templateType: 'csl', templateSection: 'csl' };
  if (filePath.includes('/template/indesign')) return { templateType: 'indesign', templateSection: 'indesign' };
  if (filePath.includes('/template/preparo')) return { templateType: 'preparo', templateSection: 'preparo' };
  return { templateType: 'other', templateSection: 'undefined' };
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Stores
  syncStatus,
  pendingChangesCount,
  lastSyncedAt,
  lastSyncError,
  isProvisioned,
  isSyncActive,
  syncConflicts,
  conflictCount,
  projectIndex,
  isOnline,
  // Lifecycle
  init,
  destroy,
  setupOfflineDetection,
  // Dirty tracking
  markProjectDirty,
  markPresetDirty,
  markUnsyncedPresetsDirty,
  syncNow,
  // Read operations
  checkStatus,
  listProjects,
  getProject,
  listPresets,
  getPreset,
  fetchProjectIndex,
  // Write operations
  pushChanges,
  deleteProjectFromGit,
  deletePresetFromGit,
  // Hydration
  hydrateProject,
  hydrateFromGit,
  // Conflict resolution
  resolveConflict,
  clearResolvedConflicts,
};
